import React, { Suspense, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Aperture,
    Armchair,
    Box as BoxIcon,
    Camera,
    Copy,
    Layers,
    Ratio,
    Ruler,
    Scan,
    SwitchCamera,
    Trash2,
    X,
} from 'lucide-react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, useTexture, Environment, Lightformer, ContactShadows, Grid, TransformControls, Html, Line } from '@react-three/drei';
import { EffectComposer, DepthOfField, BrightnessContrast } from '@react-three/postprocessing';
import * as THREE from 'three';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

import { useShotCamera, CameraObject, PiPView, CamView, EDITOR_FOCAL_LENGTH, CameraPathAnimator, ReleaseContextOnUnmount, resolveRoomInteriorAngle, parseMeasuredCamera, resolveMeasuredRoomCamera } from './StudioCamera';
import {
    PlacedProp, ScenePropObject, FloorDropResolver, describeSceneProps,
    UniformScaleHandle, PROP_SCALE_MIN, PROP_SCALE_MAX,
} from './StudioProps';
import { useStudioPropLibrary, StudioPropLibraryPanel } from './StudioPropLibrary';
import { StudioProp } from '../../../../common/hooks/use-content-studio-api.hook';
import { applyMeasuredView, lensForFov, nearestAspectPreset } from './measured-view';
import { autoFurnish, RoomKind } from './auto-furnish';
import { useLiveState } from './use-live-state';
import { hideCaptureHelpers, restoreCaptureHelpers } from './studio-capture';
import { placePiece, poseGeometry, isAirborne, PoseLayout } from './surface-pose-layout';
import { isObjectAngle, objectAperture, objectLens, objectView, piecesBounds, texturedBounds } from './surface-object-framing';
import { Move3d, recordClip } from './surface-clip-recorder';
import { getProduct } from '../../edit-product-v3/api/productV2Api';
import { StudioDock, StudioDockItemDef } from './StudioDock';
import { useStudioTakes } from './use-studio-takes';
import { takeDockItems, TakeRecordedHandler } from './TakeDockPanels';
import { surfacePieces } from '../../vcam/vcam';
// Reuse the exact furniture-studio primitives so the UI matches 1:1.
import {
    LENS_PRESETS,
    APERTURE_STOPS,
    ASPECT_PRESETS,
    aspectToNumber,
    lensCharacter,
    apertureCharacter,
    apertureToDof,
    StudioSectionHeader,
    StudioIconToggle,
    StudioMarkSlider,
    StudioPill,
    CameraRig,
    StudioRailButton,
    STUDIO_TOOLBAR_CLASS,
    StudioToolbarDivider,
} from './Furniture3DStudioWidget';

/** The seamless white a filmed tile sits on. */
const STUDIO_WHITE = '#F6F5F2';

/**
 * Surface 3D Studio — the camera-control step for SURFACE products (tiles,
 * planks, stone). Two modes:
 *   - single  : the material's real slabs (materialPaths) as movable hero
 *               objects you can select + move / rotate.
 *   - applied : the seamless texture mapped onto a room corner (floor and/or
 *               walls), shot perspective-correct from any angle with a real lens.
 * Shares the furniture studio's controls (lens + f-stop sliders, grid/gizmo
 * toggles, DOF). The capture feeds the worker's photoreal render.
 */

// Tile-size multipliers (1 = the material's real seamless size). Discrete steps
// so the slider snaps cleanly; ratio is always preserved.
const TILE_SCALES = [0.5, 0.75, 1, 1.5, 2, 3];

// A surface's material COVERAGE, in normalised surface coordinates (0..1 on each
// axis). Full = the whole surface; an "area" clad uses a sub-rectangle (a
// backsplash band, a floor inset, a feature panel) the vendor drags to size.
type Coverage = { u0: number; v0: number; u1: number; v1: number };
const FULL_COV: Coverage = { u0: 0, v0: 0, u1: 1, v1: 1 };
const CENTER_COV: Coverage = { u0: 0.3, v0: 0.3, u1: 0.7, v1: 0.7 };
const MIN_COV = 0.1; // smallest panel size (fraction of the surface)
type SurfaceKey = 'floor' | 'back' | 'left' | 'right';

interface Surface3DStudioWidgetProps {
    /** Fallback texture URL (e.g. the product thumbnail) used until the real
     *  seamless material is fetched by uuid. */
    textureUrl?: string;
    /** Product UUID — the widget fetches the authoritative seamless material(s)
     *  (visualization.surface.materialPaths) + real tile size. */
    productUuid?: string;
    /** Product application types — 'floor' / 'walls' — gate the Applied targets. */
    applicationTypes?: string[];
    /**
     * Where the recipe says the material goes ('floor' | 'walls' | 'floor-and-walls').
     *
     * The studio used to decide this from the product alone — floor whenever the
     * product allows a floor — so a plan asking for a kitchen backsplash captured
     * the tile on the floor, and the render dutifully built a kitchen with a tiled
     * floor and a plain wall. It now seeds from the request on a headless capture
     * or a first open; a vendor's own toggles in a saved session still win.
     */
    surfaceTarget?: string;
    /** Recipe aspect ratio — the viewport matches it so framing = output. */
    aspectRatio?: string;
    onAspectRatioChange?: (value: string) => void;
    /**
     * The pose chosen on the card — how many pieces, and how they stand.
     *
     * The studio builds the half of a pose that has to be real geometry; the
     * worker paints the half that cannot be (a hand, props). Changing it
     * re-lays the pieces out, which discards any hand-placed transforms —
     * choosing a new pose IS asking for a new arrangement.
     */
    poseId?: string;
    /** Shot mode — set by the recipe (Single Piece / In-Room cards). */
    mode?: 'single' | 'applied';
    onModeChange?: (mode: 'single' | 'applied') => void;
    /** When true the shot type is fixed by the recipe — hide the Shot switch. */
    lockMode?: boolean;
    /** URL of a previously captured snapshot, if any. */
    snapshotUrl?: string;
    /** Upload the captured frame; returns its URL. */
    onCapture: (file: File) => Promise<string>;
    /**
     * A named angle to pose and capture without anyone opening the dialog.
     *
     * The furniture studio has had this since plans learned to ask for an
     * angle; the surface studio did not, so a plan that set the camera on a
     * tile scene armed a capture that nothing could perform — the node sat at
     * "needs a detail" with an empty studio behind it.
     */
    headlessAngle?: string | null;
    /**
     * A camera measured from a reference photograph, as JSON. Takes precedence
     * over `headlessAngle`: a named angle is a bucket, this is the shot the
     * reference was actually taken with.
     */
    headlessCameraView?: string | null;
    onHeadlessDone?: (error?: string) => void;
    /**
     * Record a short film of the pieces instead of a still: the headless run
     * poses the shot as usual, then plays this move frame by frame and encodes
     * it (surface-clip-recorder). The clip is handed to `onClipRecorded`.
     */
    headlessClip?: { move: Move3d; seconds: number; fps?: number; width: number; height: number; backdrop?: string } | null;
    onClipRecorded?: (clip: { file: File; poster: Blob; width: number; height: number; seconds: number }) => Promise<void> | void;
    /** Persist the uploaded URL (+ camera meta) onto the recipe node's inputs. */
    /**
     * The framing this node was last left at, read back from the node itself.
     *
     * The studio used to remember it only in a ref on a mounted component, so
     * it survived closing the dialog but nothing else — not a re-render that
     * unmounted the widget, and not a page reload. An agent-driven capture
     * mounts the studio, poses it and closes it again, and the framing it set
     * has to outlive all of that.
     */
    initialCamera?: {
        pos: [number, number, number];
        target: [number, number, number];
        /** How much of the room was in the picture. Without it the camera goes
         *  back to the right place and shows a different shot from it. */
        fovDegV?: number;
    };
    onSnapshotUpdated: (url: string, meta?: { lensMm: number; aperture: number; aspectRatio: string; mode: string; cameraView?: string; camera?: { pos: [number, number, number]; target: [number, number, number]; fovDegV?: number } | null; matFloor?: boolean; matWalls?: boolean; appliedInset?: boolean; pieces?: number; sceneProps?: Array<{ name: string; category: string }> }) => void;
    /** Draw your own launcher instead of the thumbnail tile; call `open` to open the studio. */
    renderTrigger?: (open: () => void) => React.ReactNode;
    /** Open the studio with this dock panel already showing (e.g. 'video', 'phone'). */
    initialDock?: string;
    /**
     * A recorded camera move over the pieces, offered back as a "take" — the
     * same contract as the furniture studio's. Single-piece mode only: that is
     * where the tiles are objects a camera can move around.
     */
    onTakeRecorded?: TakeRecordedHandler;
}

interface PieceTransform {
    pos: [number, number, number];
    rot: [number, number, number];
    scale: [number, number, number];
}

interface SurfaceStudioSession {
    /** The SHOT camera (what gets captured). */
    camera?: CamView;
    /** The free editor camera (Phase 3 — edit the scene without moving the shot). */
    editCamera?: CamView;
    pieceTransforms?: PieceTransform[];
    /** How the pieces are arranged — set by the card's pose. */
    layout?: PoseLayout;
    /** Bearing the floor material is laid at, degrees — measured from a reference. */
    gridBearing?: number;
    /**
     * The measured field of view, in degrees.
     *
     * Part of the framing, not a detail of it: where the camera stands and
     * where it looks say nothing about how much of the room is in the picture.
     * Left out, a reopened session put the camera back in the right place and
     * then showed a different shot from it.
     */
    measuredFovDegV?: number;
    mode?: 'single' | 'applied';
    productUuid?: string;
    matFloor?: boolean;
    matBack?: boolean;
    matLeft?: boolean;
    matRight?: boolean;
    // Per-surface "map only an area" flag + the dragged region (normalised).
    areaFloor?: boolean;
    areaBack?: boolean;
    areaLeft?: boolean;
    areaRight?: boolean;
    covFloor?: Coverage;
    covBack?: Coverage;
    covLeft?: Coverage;
    covRight?: Coverage;
    sceneProps?: PlacedProp[];
    pieces?: number;
    floorW?: number;
    floorD?: number;
    wallH?: number;
    tileScale?: number;
    wallBack?: boolean;
    wallLeft?: boolean;
    wallRight?: boolean;
    tileW?: number;
    tileH?: number;
    seamlessW?: number | null;
    seamlessH?: number | null;
    thicknessMm?: number;
    focalLength?: number;
    aperture?: number;
    aspectRatioSel?: string;
}

export const Surface3DStudioWidget: React.FC<Surface3DStudioWidgetProps> = (props) => {
    const { snapshotUrl, initialCamera, renderTrigger } = props;
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    // Settings live here across open/close (only the Dialog remounts). The
    // FRAMING is seeded from the node, so it also survives this component
    // being unmounted and a page reload — see `initialCamera`.
    const sessionRef = useRef<SurfaceStudioSession>({
        camera: initialCamera ? { pos: initialCamera.pos, target: initialCamera.target } : undefined,
        measuredFovDegV: initialCamera?.fovDegV,
    });
    useEffect(() => {
        // The node's own camera may arrive after the first render (a graph
        // loaded from the server). Take it only while nothing better is known,
        // so a framing set in this session is never overwritten by the older
        // one it came from.
        if (initialCamera && !sessionRef.current.camera) {
            sessionRef.current.camera = { pos: initialCamera.pos, target: initialCamera.target };
            sessionRef.current.measuredFovDegV = initialCamera.fovDegV;
        }
    }, [initialCamera]);

    return (
        <>
            {renderTrigger ? renderTrigger(() => setOpen(true)) : <div
                className="group relative flex aspect-square w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg border bg-muted transition-colors hover:border-input"
                onClick={() => setOpen(true)}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {snapshotUrl ? (
                    <img
                        src={snapshotUrl}
                        alt="Captured view"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                ) : (
                    <div className="text-muted-foreground opacity-40 [&_svg]:block">
                        <svg width="46" height="46" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="40.887 14.25 24 4.5 7.113 14.25 7.113 33.75 24 43.5 40.887 33.75 40.887 14.25" />
                            <polyline points="7.113 14.25 24 24 40.887 14.25" />
                            <line x1="24" y1="43.5" x2="24" y2="24" />
                            <polygon points="31.12 11.668 31.12 28.111 16.88 36.332 16.88 19.889 31.12 11.668" />
                        </svg>
                    </div>
                )}
                <div className="absolute bottom-2.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-lg border bg-background/90 px-2.5 py-1 text-xs font-medium whitespace-nowrap text-foreground backdrop-blur-md transition-colors group-hover:border-input">
                    <Scan className="size-3.5" />
                    {snapshotUrl ? t('CONTENT_STUDIO.STUDIO_REFRAME', 'Re-frame') : t('CONTENT_STUDIO.STUDIO_OPEN', 'Open 3D Studio')}
                </div>
            </div>}
            {(open || !!props.headlessAngle || !!props.headlessCameraView) && (
                <StudioDialog
                    open={open || !!props.headlessAngle || !!props.headlessCameraView}
                    onClose={() => setOpen(false)}
                    sessionRef={sessionRef}
                    {...props}
                />
            )}
        </>
    );
};

// ─── The dialog ─────────────────────────────────────────────────────────

const StudioDialog: React.FC<{ open: boolean; onClose: () => void; sessionRef: React.MutableRefObject<SurfaceStudioSession> } & Surface3DStudioWidgetProps> = ({
    open,
    onClose,
    sessionRef,
    textureUrl,
    productUuid,
    applicationTypes,
    surfaceTarget,
    aspectRatio,
    onAspectRatioChange,
    poseId,
    mode: modeProp,
    onModeChange,
    lockMode,
    onCapture,
    headlessAngle,
    headlessCameraView,
    onHeadlessDone,
    headlessClip,
    onClipRecorded,
    onSnapshotUpdated,
    initialDock,
    onTakeRecorded,
}) => {
    const s0 = sessionRef.current;
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.Camera | null>(null);
    const controlsRef = useRef<any>(null);

    const allowsFloor = !applicationTypes || applicationTypes.length === 0 || applicationTypes.includes('floor');
    const allowsWalls = !applicationTypes || applicationTypes.length === 0 || applicationTypes.includes('walls') || applicationTypes.includes('wall');

    const [mode, setModeLocal] = useState<'single' | 'applied'>(s0.mode || modeProp || 'single');
    useEffect(() => { if (modeProp && modeProp !== mode) setModeLocal(modeProp); /* eslint-disable-next-line */ }, [modeProp]);
    const setMode = (m: 'single' | 'applied') => { setModeLocal(m); onModeChange?.(m); };

    // Which surfaces carry the material — floor and/or any individual wall. The
    // others stay neutral placeholders the worker reskins into the scene. Restored
    // from the session ONLY for the same product (a swapped product resets to its
    // own capabilities so a floor-only product can't stay mapped to walls).
    const sameProduct = s0.productUuid === productUuid;
    const target = (surfaceTarget || '').toLowerCase();
    const targetWalls = /wall/.test(target);
    const targetFloor = !target || /floor/.test(target);
    // The request decides when nothing better is known: a headless (agent) capture,
    // or a product never opened in this session.
    const fromTarget = !!target && (!!headlessAngle || !!headlessCameraView || !sameProduct || s0.matFloor === undefined);
    const initMat = (saved: boolean | undefined, fallback: boolean, allowed: boolean) =>
        allowed ? (fromTarget ? fallback : (sameProduct ? (saved ?? fallback) : fallback)) : false;
    const [matFloor, setMatFloor] = useState<boolean>(() => initMat(s0.matFloor, fromTarget ? (targetFloor || !allowsWalls) : true, allowsFloor));
    const [matBack, setMatBack] = useState<boolean>(() => initMat(s0.matBack, fromTarget ? targetWalls : (allowsWalls && !allowsFloor), allowsWalls));
    const [matLeft, setMatLeft] = useState<boolean>(() => initMat(s0.matLeft, false, allowsWalls));
    const [matRight, setMatRight] = useState<boolean>(() => initMat(s0.matRight, false, allowsWalls));
    const matWalls = matBack || matLeft || matRight;
    // "Map only an area" per surface — a clad surface can carry the material on
    // its WHOLE face (full) or on a dragged sub-rectangle (area / inset). Restored
    // only for the same product (a swap resets to full coverage).
    const initArea = (saved: boolean | undefined) => (sameProduct ? (saved ?? false) : false);
    const initCov = (saved: Coverage | undefined) => (sameProduct && saved ? saved : { ...FULL_COV });
    const [areaFloor, setAreaFloor] = useState<boolean>(() => initArea(s0.areaFloor));
    const [areaBack, setAreaBack] = useState<boolean>(() => initArea(s0.areaBack));
    const [areaLeft, setAreaLeft] = useState<boolean>(() => initArea(s0.areaLeft));
    const [areaRight, setAreaRight] = useState<boolean>(() => initArea(s0.areaRight));
    const [covFloor, setCovFloor] = useState<Coverage>(() => initCov(s0.covFloor));
    const [covBack, setCovBack] = useState<Coverage>(() => initCov(s0.covBack));
    const [covLeft, setCovLeft] = useState<Coverage>(() => initCov(s0.covLeft));
    const [covRight, setCovRight] = useState<Coverage>(() => initCov(s0.covRight));
    // Flip a surface between Full and Area coverage; entering Area seeds a centred
    // region if the rect is still full.
    const toggleArea = (
        area: boolean, setArea: React.Dispatch<React.SetStateAction<boolean>>,
        cov: Coverage, setCov: React.Dispatch<React.SetStateAction<Coverage>>,
    ) => {
        const next = !area;
        if (next && cov.u0 === 0 && cov.v0 === 0 && cov.u1 === 1 && cov.v1 === 1) setCov({ ...CENTER_COV });
        setArea(next);
    };
    const [pieces, setPieces] = useState(s0.pieces ?? poseGeometry(poseId).pieces);
    const [layout, setLayout] = useState<PoseLayout>(s0.layout || poseGeometry(poseId).layout);
    /**
     * A pose change re-lays the pieces out and drops the saved transforms.
     *
     * Not on mount, though: reopening the studio must find the shot where it
     * was left, gizmo nudges and all. Only an actual change to the pose means
     * "arrange them differently".
     */
    const lastPoseRef = useRef<string | undefined>(poseId);
    useEffect(() => {
        if (poseId === lastPoseRef.current) return;
        lastPoseRef.current = poseId;
        const g = poseGeometry(poseId);
        setPieces(g.pieces);
        setLayout(g.layout);
        // The slabs restore their saved transform once, on mount — so a new
        // arrangement means clearing the old transforms AND remounting them.
        sessionRef.current.pieceTransforms = undefined;
        setPoseNonce((n) => n + 1);
        setSelectedObj(null);
    }, [poseId]);
    /** Bumped on a pose change to remount the pieces at their new places. */
    const [poseNonce, setPoseNonce] = useState(0);
    // Applied room STRUCTURE — the vendor designs the walls + freely resizes the
    // floor / walls by dragging their edge handles in the 3D scene. The capture
    // keeps this exact structure + camera; the worker repaints the placeholder
    // walls into the chosen environment (keeping the floor + geometry).
    /** Bearing of the reference floor's grid, from the measurement. */
    // Live: the headless pass sets this and then captures, and the session it
    // writes afterwards has to record the bearing it just applied — not the one
    // it replaced. A floor laid diagonally came back straight on reopen.
    const [gridBearing, setGridBearing, gridBearingRef] = useLiveState<number>(s0.gridBearing ?? 0);
    const [floorW, setFloorW] = useState<number>(s0.floorW ?? 6);
    const [floorD, setFloorD] = useState<number>(s0.floorD ?? 6);
    const [wallH, setWallH] = useState<number>(s0.wallH ?? 3);
    // Tile-size multiplier (1 = real seamless size). Lets the vendor make the
    // tiles read bigger / smaller to suit the framing, keeping the ratio.
    const [tileScale, setTileScale] = useState<number>(s0.tileScale ?? 1);
    const [wallBack, setWallBack] = useState<boolean>(s0.wallBack ?? true);
    const [wallLeft, setWallLeft] = useState<boolean>(s0.wallLeft ?? true);
    const [wallRight, setWallRight] = useState<boolean>(s0.wallRight ?? false);
    // Any clad + PRESENT surface mapped on just an area (drives the worker's inset
    // reskin: keep the textured region, repaint the surrounding grey).
    const anyInset = (matFloor && areaFloor) || (matBack && wallBack && areaBack)
        || (matLeft && wallLeft && areaLeft) || (matRight && wallRight && areaRight);
    // Click-a-surface in the viewport toggles its material — same rules as the
    // dock's Material panel: only allowed surfaces, keep at least one clad.
    const toggleSurfaceMat = (s: SurfaceKey) => {
        // A surface click first dismisses a selected piece (so you can click off
        // it); it toggles the material only when nothing is selected.
        if (selPropId) { setSelPropId(null); return; }
        const cladCount = (matFloor ? 1 : 0) + (matBack && wallBack ? 1 : 0) + (matLeft && wallLeft ? 1 : 0) + (matRight && wallRight ? 1 : 0);
        const cur = s === 'floor' ? matFloor : s === 'back' ? matBack : s === 'left' ? matLeft : matRight;
        const allowed = s === 'floor' ? allowsFloor : allowsWalls;
        if (!allowed) return;
        if (cur && cladCount <= 1) return;
        const set = s === 'floor' ? setMatFloor : s === 'back' ? setMatBack : s === 'left' ? setMatLeft : setMatRight;
        set((v) => !v);
    };
    // Real tile dimensions (metres), taken from the product's width × height.
    const [tileW, setTileW] = useState(s0.tileW ?? 0.6);
    const [tileH, setTileH] = useState(s0.tileH ?? 0.6);
    const [thicknessMm, setThicknessMm] = useState(s0.thicknessMm ?? 10); // slab thickness (mm)
    const [showGrid, setShowGrid] = useState(true);
    const [showGizmo, setShowGizmo] = useState(true);
    const [transformMode, setTransformMode] = useState<'translate' | 'rotate'>('translate');
    const [selectedObj, setSelectedObj] = useState<THREE.Object3D | null>(null);
    const [focalLength, applyFocalLength, focalLengthRef] = useLiveState(s0.focalLength ?? 35);
    const setFocalLength = applyFocalLength;
    /**
     * The field of view a reference was measured at, in degrees.
     *
     * Held as an angle and re-asserted every frame, because routing it through
     * the lens control makes it depend on the aspect ratio at two different
     * moments — the one when millimetres are computed, and the one when
     * three.js turns them back into an angle. Between those two moments the
     * studio canvas can be resized by the recipe's own aspect ratio, and the
     * shot silently opens up. Null once the vendor takes the lens themselves.
     */
    const measuredFovRef = useRef<number | null>(s0.measuredFovDegV ?? null);
    /** Vendor's hand on the lens: the measurement is theirs to override. */
    const takeLens = useCallback((mm: number) => {
        measuredFovRef.current = null;
        applyFocalLength(mm);
    }, [applyFocalLength]);
    const [aperture, setAperture] = useState(s0.aperture ?? 16);
    // The frame ratio is shared with the recipe — the recipe's value wins on
    // mount, and a live effect keeps the studio in sync if it changes there.
    const [aspectRatioSel, setAspectRatioSel, aspectRatioSelRef] = useLiveState(aspectRatio || s0.aspectRatioSel || '4:5');
    useEffect(() => {
        if (aspectRatio && aspectRatio !== aspectRatioSel) setAspectRatioSel(aspectRatio);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aspectRatio]);
    // Camera position/target captured from the last session (restored on mount).
    const savedCameraRef = useRef(sessionRef.current.camera || null);
    // ── Phase 3: the SHOT camera is separate from the editor view. 'shot' =
    // looking through the camera (framing, today's behaviour — always the mode a
    // session opens in); 'edit' = free view to work on the scene while the shot
    // stays framed (a camera object + live PiP preview show the shot).
    const { camMode, setCamMode, shotViewRef, toggleCamMode, currentShotView, shotCameraViewText, captureFromShot, saveViews } = useShotCamera({
        cameraRef, controlsRef, initialShot: s0.camera || null, initialEdit: s0.editCamera || null,
    });
    // Free view: pose the camera object with a move/rotate gizmo.
    const [camGizmoMode, setCamGizmoMode] = useState<'translate' | 'rotate'>('translate');

    // ── Real library furniture placed in the built room
    // should place real pieces. Placed on the floor, moved/rotated/scaled with a
    // gizmo, and captured (the worker gets a typed list of what goes where).
    /**
     * Real furniture in the built room, replacing the grey blockout masses.
     *
     * A blockout said "put SOMETHING like a sofa here" and left the model to
     * invent it. A prop IS the sofa, so the render's job drops from inventing a
     * piece to lighting one — the same trade the furniture studio made, and the
     * reason blockouts are gone from here too.
     */
    const [sceneProps, setSceneProps] = useState<PlacedProp[]>(() => (sameProduct && (s0 as any).sceneProps) ? (s0 as any).sceneProps : []);
    const [selPropId, setSelPropId] = useState<string | null>(null);
    const [selPropObj, setSelPropObj] = useState<THREE.Object3D | null>(null);
    const [propGizmo, setPropGizmo] = useState<'translate' | 'rotate'>('translate');
    const propRefs = useRef<Map<string, THREE.Group>>(new Map());
    const propSeq = useRef(0);
    /** Set by a component inside the Canvas — see FloorDropResolver. */
    const floorDropRef = useRef<((x: number, y: number) => [number, number, number] | null) | null>(null);
    const placeProp = (item: StudioProp, at?: [number, number, number]) => {
        const shot = currentShotView();
        const fallback: [number, number, number] = [
            shot ? Math.max(-3, Math.min(3, shot.target[0] + 1.2)) : 1.4,
            0,
            shot ? Math.max(-3, Math.min(3, shot.target[2])) : 0.4,
        ];
        const id = `prop-${propSeq.current++}`;
        setSceneProps((l) => [...l, {
            id, propId: item.propId, name: item.name, category: item.category,
            pos: at || fallback, rotY: 0, scale: 1,
        }]);
        setSelPropId(id);
    };
    const deleteProp = (id: string) => {
        setSceneProps((l) => l.filter((p) => p.id !== id));
        if (selPropId === id) { setSelPropId(null); setSelPropObj(null); }
    };
    const commitPropTransform = () => {
        const g = selPropObj;
        if (!g || !selPropId) return;
        const sc = Math.max(PROP_SCALE_MIN, Math.min(PROP_SCALE_MAX, g.scale.x));
        setSceneProps((l) => l.map((p) => (
            p.id === selPropId ? { ...p, pos: [g.position.x, 0, g.position.z], rotY: g.rotation.y, scale: sc } : p
        )));
    };
    useEffect(() => { if (!selPropId) setSelPropObj(null); }, [selPropId]);
    // Live map of single-piece slab groups (by index) so their gizmo transforms
    // can be saved + restored.
    const piecesRef = useRef<Map<number, THREE.Object3D>>(new Map());
    const registerPiece = React.useCallback((i: number, obj: THREE.Object3D | null) => {
        if (obj) piecesRef.current.set(i, obj); else piecesRef.current.delete(i);
    }, []);
    const [capturing, setCapturing] = useState(false);
    /** Filming a clip: the ground and background go studio white, helpers away. */
    const [recording, setRecording] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /**
     * The shared furniture shelf, declared here because it reports failures
     * through the studio's own error banner — so it has to come after it.
     */
    /** Which left panel is open, if any. Null is the bare viewport. */
    const [leftPanel, setLeftPanel] = useState<'furniture' | null>(null);
    const propLibrary = useStudioPropLibrary(setError);
    // Fetched when the studio opens, not when the panel does: the dock has no
    // open callback, and the list is picker metadata — small enough to warm.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { propLibrary.open(); }, []);
    const focusTarget = useMemo(() => new THREE.Vector3(0, 0.2, 0), []);
    const dof = apertureToDof(aperture);

    // ── Takes — keyframed or phone-filmed camera moves around the pieces,
    // recorded off the viewport. The same machinery as the furniture studio
    // (use-studio-takes); the scene here is in real metres, so the phone aims
    // at tile height rather than a sofa's.
    const takeLookRef = useRef<{ background: THREE.Scene['background']; grid: boolean; gizmo: boolean } | null>(null);
    const takes = useStudioTakes({
        open, productUuid, rendererRef, sceneRef, cameraRef, controlsRef,
        shot: { camMode, setCamMode, toggleCamMode, shotViewRef, currentShotView },
        focalLength, setLens: takeLens, capturing, onCapture,
        subjectHeight: 0.1,
        // Shot as the headless clip recorder shoots: the pieces on a seamless
        // studio white, no grid and no gizmo.
        look: {
            enter: async (_renderer, scene) => {
                takeLookRef.current = { background: scene?.background ?? null, grid: showGrid, gizmo: showGizmo };
                setShowGrid(false);
                setShowGizmo(false);
                setRecording(true);
                if (scene) scene.background = new THREE.Color(STUDIO_WHITE);
                await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
            },
            exit: (_renderer, scene) => {
                const was = takeLookRef.current;
                takeLookRef.current = null;
                if (scene) scene.background = was?.background ?? null;
                setRecording(false);
                setShowGrid(was?.grid ?? true);
                setShowGizmo(was?.gizmo ?? true);
            },
        },
        // A camera move is the vendor's hand on the lens: a measured field of
        // view held every frame would otherwise fight the fly-through.
        beforeFly: () => { measuredFovRef.current = null; },
        phoneSubject: () => ({
            surface: mode === 'single'
                ? { tileW, tileH, thickness: thicknessMm / 1000, pieces: surfacePieces(piecesRef.current) }
                : undefined,
        }),
    });

    // Authoritative materials + real sizes, fetched by uuid.
    //  - Applied  → the SEAMLESS texture (materialPath), tiled at its real-world
    //    coverage (seamlessParams.textureWidth/Height in metres).
    //  - Single   → the individual plank images (materialPaths), sized per tile.
    const [resolvedTexture, setResolvedTexture] = useState<string | undefined>(textureUrl);
    // Read live by the headless capture's wait loop. Its closure saw the value
    // at the moment it started — empty whenever the texture came from the
    // product fetch rather than a prop — and waited 30 s for a texture it
    // already had (12 Oct 2026).
    const resolvedTextureRef = useRef(resolvedTexture);
    resolvedTextureRef.current = resolvedTexture;
    const [materialPaths, setMaterialPaths] = useState<string[]>(textureUrl ? [textureUrl] : []);
    // Real-world span (metres) of ONE repeat of the seamless texture. Falls back
    // to the single-tile size when the product carries no seamless dimensions.
    const [seamlessW, setSeamlessW] = useState<number | null>(s0.seamlessW ?? null);
    const [seamlessH, setSeamlessH] = useState<number | null>(s0.seamlessH ?? null);
    useEffect(() => {
        if (!open) return;
        setResolvedTexture(textureUrl);
        setMaterialPaths(textureUrl ? [textureUrl] : []);
        if (!productUuid) return;
        let cancelled = false;
        getProduct(productUuid).then((p: any) => {
            if (cancelled) return;
            const surf = p?.visualization?.surface
                || p?.visualization?.data?.find?.((d: any) => d?.type === 'surface')?.surface;
            // Applied wants the seamless texture (materialPath); single wants the planks.
            const seamless = surf?.materialPath
                || (Array.isArray(surf?.materialPaths) ? surf.materialPaths.filter(Boolean)[0] : undefined);
            const paths: string[] = Array.isArray(surf?.materialPaths) && surf.materialPaths.length
                ? surf.materialPaths.filter(Boolean)
                : (surf?.materialPath ? [surf.materialPath] : []);
            if (seamless) setResolvedTexture(seamless);
            if (paths.length) setMaterialPaths(paths);
            // Seamless real-world coverage (metres) → drives the applied tiling density.
            const sp = surf?.seamlessParams;
            const sw = Number(sp?.textureWidth);
            const sh = Number(sp?.textureHeight);
            if (sw > 0 && sw <= 12) setSeamlessW(Number(sw.toFixed(3)));
            if (sh > 0 && sh <= 12) setSeamlessH(Number(sh.toFixed(3)));
            // Tile dimensions come straight from the product's real size.
            const size = p?.axes?.size;
            const unit = (size?.unit || 'cm').toString();
            const toMetres = (v: any) => {
                const n = Number(v);
                if (!(n > 0)) return null;
                return unit === 'mm' ? n / 1000 : unit === 'm' ? n : n / 100;
            };
            const wM = toMetres(size?.width);
            const hM = toMetres(size?.height);
            if (wM && wM >= 0.02 && wM <= 4) setTileW(Number(wM.toFixed(3)));
            if (hM && hM >= 0.02 && hM <= 4) setTileH(Number(hM.toFixed(3)));
            else if (wM) setTileH(Number(wM.toFixed(3))); // square fallback when no height
            // Thickness → the depth dimension (converted to mm), unless the
            // vendor already set it this session.
            if (s0.thicknessMm == null) {
                const dMm = (() => {
                    const d = Number(size?.depth);
                    if (!(d > 0)) return null;
                    return unit === 'mm' ? d : unit === 'm' ? d * 1000 : d * 10;
                })();
                if (dMm && dMm >= 2 && dMm <= 60) setThicknessMm(Math.round(dMm));
            }
        }).catch(() => { /* keep the fallback texture */ });
        return () => { cancelled = true; };
    }, [open, productUuid, textureUrl]);

    const aspect = aspectToNumber(aspectRatioSel);

    // Persist camera + settings so re-opening restores the exact framing. The
    // SHOT view is saved as `camera` (what RestoreCameraSurface reloads — a
    // session always reopens looking through the camera); the free editor view
    // is kept separately.
    const saveSession = () => {
        const { shot: shotView, edit: editView } = saveViews();
        const pieceTransforms: PieceTransform[] = [];
        piecesRef.current.forEach((o, i) => {
            pieceTransforms[i] = {
                pos: [o.position.x, o.position.y, o.position.z],
                rot: [o.rotation.x, o.rotation.y, o.rotation.z],
                scale: [o.scale.x, o.scale.y, o.scale.z],
            };
        });
        sessionRef.current = {
            camera: shotView || sessionRef.current.camera,
            editCamera: editView || sessionRef.current.editCamera,
            pieceTransforms: pieceTransforms.length ? pieceTransforms : sessionRef.current.pieceTransforms,
            mode, productUuid, matFloor, matBack, matLeft, matRight,
            areaFloor, areaBack, areaLeft, areaRight, covFloor, covBack, covLeft, covRight, sceneProps,
            pieces, layout, floorW, floorD, wallH, tileScale, wallBack, wallLeft, wallRight, tileW, tileH, seamlessW, seamlessH, thicknessMm,
            focalLength: focalLengthRef.current,
            aperture,
            aspectRatioSel: aspectRatioSelRef.current,
            gridBearing: gridBearingRef.current,
            measuredFovDegV: measuredFovRef.current ?? sessionRef.current.measuredFovDegV,
        };
    };
    const handleClose = () => { saveSession(); onClose(); };

    /**
     * Pose to a named angle and capture, with nobody at the controls.
     *
     * Unlike the furniture studio there is no model to await — the room shell
     * is synchronous geometry — but the texture is not: capturing before it
     * resolves uploads a frame of untextured grey that looks exactly like a
     * successful capture. So this waits for the renderer, the camera and the
     * texture, then poses, then captures.
     *
     * The room is centred on the origin (floor at y=0, back wall at -D/2), so
     * the subject the angle frames is the room's own volume — the camera lands
     * outside the open side looking in, which is the room-corner shot.
     */
    useEffect(() => {
        const measured = parseMeasuredCamera(headlessCameraView);
        // A capture that cannot be performed must still be reported. The
        // dialog opens on the mere PRESENCE of headlessCameraView, so bailing
        // out quietly here — which is what an unparseable value did — left the
        // studio open with nothing running and no way to close it.
        if (open && headlessCameraView && !measured && !headlessAngle) {
            onHeadlessDone?.('That measured camera could not be read');
            return undefined;
        }
        if (!open || (!headlessAngle && !measured)) return undefined;
        let cancelled = false;

        /**
         * Is the floor actually drawn?
         *
         * A resolved texture URL is not a drawn floor: three.js still has to
         * decode the image, build the material and render a frame. Waiting on
         * the URL captured an empty buffer — which uploads happily and reaches
         * the vendor looking like a successful shot of nothing.
         */
        const floorIsDrawn = () => {
            const scene = sceneRef.current;
            if (!scene) return false;
            let found = false;
            scene.traverse((o: any) => {
                if (found || !o.isMesh || !o.visible) return;
                const mats = Array.isArray(o.material) ? o.material : [o.material];
                if (mats.some((m: any) => m?.map?.image)) found = true;
            });
            return found;
        };

        (async () => {
            const deadline = Date.now() + 30_000;
            while (!cancelled && Date.now() < deadline) {
                if (rendererRef.current && cameraRef.current && resolvedTextureRef.current && floorIsDrawn()) break;
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 120));
            }
            if (cancelled) return;
            if (!rendererRef.current || !cameraRef.current || !resolvedTextureRef.current || !floorIsDrawn()) {
                // Say which part never arrived — "did not load" alone sent every
                // investigation to the network, when it was once the texture.
                const missing = [
                    !rendererRef.current && 'renderer',
                    !cameraRef.current && 'camera',
                    !resolvedTextureRef.current && 'product texture',
                    !floorIsDrawn() && 'textured pieces drawn',
                ].filter(Boolean).join(', ');
                onHeadlessDone?.(`The surface studio did not finish loading in time (missing: ${missing})`);
                return;
            }
            const canvas = rendererRef.current.domElement;
            if (!canvas.width || !canvas.height) {
                onHeadlessDone?.('The 3D view had no size to capture from');
                return;
            }

            const camNow = cameraRef.current as any;
            // A measured camera wins: it is the reference's actual shot rather
            // than the nearest name for it.
            let view = measured ? resolveMeasuredRoomCamera(measured, floorW, floorD) : null;
            let wantAspect: number | null = null;
            if (measured?.aspect) {
                // Shoot at the reference's own shape. A vertical field of view
                // reproduces a framing only on a frame of the same shape, so
                // matching 67 degrees on a square canvas shows half a room more
                // to the sides — the angle right and the capture "too far
                // away", which is exactly what it looked like.
                const preset = nearestAspectPreset(measured.aspect, ASPECT_PRESETS);
                if (preset) {
                    wantAspect = aspectToNumber(preset);
                    if (preset !== aspectRatioSel) {
                        setAspectRatioSel(preset);
                        onAspectRatioChange?.(preset);
                    }
                }
            }
            if (measured?.gridBearingDeg) {
                // Lay the material the way the reference's is laid, before the
                // capture reads it.
                setGridBearing(measured.gridBearingDeg);
            }
            if (view?.fovDegV && camNow?.isPerspectiveCamera) {
                // A field of view, converted through three's own film model so
                // the lens control reproduces it exactly instead of overwriting
                // it. Millimetres measured against one film size and read back
                // against another cost up to 21 degrees of view on these
                // portrait references — see measured-camera-fidelity.test.ts.
                measuredFovRef.current = view.fovDegV;
                applyFocalLength(lensForFov(view.fovDegV, camNow.aspect));
            } else if (measured?.lensMm) {
                // The studio's own lens control, so the capture and the saved
                // session agree with what was measured.
                applyFocalLength(measured.lensMm);
            }
            if (!view && mode === 'single' && isObjectAngle(headlessAngle)) {
                // Tiles as objects — a packshot, a board, a macro: framed on the
                // pieces themselves, with the lens and f-stop the shot calls for.
                applyFocalLength(objectLens(headlessAngle));
                setAperture(objectAperture(headlessAngle));
                await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
                if (cancelled) return;
                // The registered pieces only: the soft contact shadow is a 10 m
                // plane that also carries a texture, and framing it put the
                // tile a speck in the middle of the picture.
                const pieceList = Array.from(piecesRef.current.values()).filter(Boolean) as THREE.Object3D[];
                const box = pieceList.length ? piecesBounds(pieceList) : texturedBounds(sceneRef.current);
                if (box && camNow?.isPerspectiveCamera) {
                    // The field of view of the lens just chosen, through three's
                    // own film model — the camera itself may not have caught up yet,
                    // and framing with the previous, wider lens put the tile
                    // edge to edge.
                    const lens = objectLens(headlessAngle);
                    const filmHeight = (camNow.filmGauge || 35) / Math.max(camNow.aspect || 1, 1);
                    const fovDeg = THREE.MathUtils.radToDeg(2 * Math.atan(filmHeight / (2 * lens)));
                    view = objectView(headlessAngle, box, { fovDeg, aspect: camNow.aspect }, { w: tileW, h: tileH });
                }
            }
            if (!view) {
                // Interior framing, not object framing: fitting the room's whole
                // shell in frame produced a diorama floating in grid space, when
                // the shot a reference implies is a person standing in the room.
                view = resolveRoomInteriorAngle(headlessAngle, floorW, floorD, wallH,
                    camNow?.isPerspectiveCamera ? { fovDeg: camNow.fov, aspect: camNow.aspect } : undefined);
            }
            if (!view) {
                onHeadlessDone?.(`"${headlessAngle}" is not an angle the studio knows`);
                return;
            }

            const controls = controlsRef.current;
            if (controls) {
                // Orbit limits guard a hand on the controls; applied to a named
                // angle they silently answer a different question (top-down
                // clamps to mid-height). Lift them for the pose.
                controls.minPolarAngle = 0;
                controls.maxPolarAngle = Math.PI;
                controls.minDistance = 0;
                controls.maxDistance = Infinity;
                camNow.position.set(view.pos[0], view.pos[1], view.pos[2]);
                controls.target.set(view.target[0], view.target[1], view.target[2]);
                controls.update();
            } else {
                camNow.position.set(view.pos[0], view.pos[1], view.pos[2]);
                camNow.lookAt(new THREE.Vector3(view.target[0], view.target[1], view.target[2]));
            }
            // Roll last: both branches above level the camera, so a reference
            // that leans has to be leaned back afterwards or it is simply lost.
            // (A hand on the orbit controls re-levels it; that is the vendor
            // overriding the measurement, which is allowed.)
            applyMeasuredView(camNow, { ...view, fovDegV: undefined });
            camNow.updateMatrixWorld(true);
            // The capture reads the SHOT view; the pose above IS the shot now.
            shotViewRef.current = { pos: view.pos, target: view.target };

            await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
            if (cancelled) return;

            // The canvas has to REACH the reference's shape before the shutter,
            // or the picture is the right framing on the wrong frame. Changing
            // it goes through React, CSS and a resize observer, so wait for the
            // camera to report the new shape rather than guess at a number of
            // frames.
            if (wantAspect) {
                for (let i = 0; i < 40 && !cancelled; i += 1) {
                    if (Math.abs((camNow.aspect || 0) - wantAspect) < 0.02) break;
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise<void>((r) => requestAnimationFrame(() => r()));
                }
                if (cancelled) return;
            }

            // The blank-frame check lives inside captureAndUse, where the real
            // capture frame exists. Checking here would sample the editor's
            // own view — grid, gizmo and backdrop — which is never blank and
            // so could only ever pass.
            try {
                if (headlessClip) await recordAndUse(headlessClip);
                else await captureAndUse();
                onHeadlessDone?.();
            } catch (err: any) {
                onHeadlessDone?.(err?.message || 'Capture failed');
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, headlessAngle, headlessCameraView]);

    /**
     * Does the drawing buffer contain anything but the background?
     *
     * Sampled through the GL context rather than by decoding a PNG, because
     * this has to answer before the upload, not after. A grid of samples that
     * all match each other is a flat frame: no floor, no room, nothing worth
     * sending.
     */
    const frameHasContent = (): boolean => {
        const renderer = rendererRef.current;
        const canvas = renderer?.domElement;
        if (!renderer || !canvas || !canvas.width || !canvas.height) return false;
        try {
            const gl = renderer.getContext();
            const step = 8;
            const cols = Math.max(2, Math.min(step, Math.floor(canvas.width / 8)));
            const rows = Math.max(2, Math.min(step, Math.floor(canvas.height / 8)));
            const px = new Uint8Array(4);
            let first: string | null = null;
            for (let r = 1; r < rows; r += 1) {
                for (let c = 1; c < cols; c += 1) {
                    const x = Math.floor((canvas.width * c) / cols);
                    const y = Math.floor((canvas.height * r) / rows);
                    gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
                    const key = `${px[0] >> 3},${px[1] >> 3},${px[2] >> 3},${px[3] >> 3}`;
                    if (first === null) first = key;
                    else if (key !== first) return true;
                }
            }
            return false;
        } catch {
            // If the buffer cannot be read, do not block the capture on a
            // check that failed for its own reasons.
            return true;
        }
    };

    /** Film the posed pieces on a clean studio white, then hand the clip over. */
    const recordAndUse = async (clip: NonNullable<typeof headlessClip>) => {
        const renderer = rendererRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current as THREE.PerspectiveCamera | null;
        if (!renderer || !scene || !camera) throw new Error('The 3D view is not ready to film');
        const nextFrame = () => new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
        setShowGrid(false);
        setShowGizmo(false);
        setRecording(true);
        const background = scene.background;
        scene.background = new THREE.Color(headlessClip?.backdrop || STUDIO_WHITE);
        await nextFrame();
        try {
            const pieces = Array.from(piecesRef.current.values()).filter(Boolean) as THREE.Object3D[];
            const result = await recordClip({
                ...clip,
                canvas: renderer.domElement,
                renderer: renderer as THREE.WebGLRenderer,
                camera,
                controls: controlsRef.current,
                scene,
                pieces,
                nextFrame,
            });
            await onClipRecorded?.(result);
        } finally {
            scene.background = background;
            setRecording(false);
            setShowGrid(true);
            setShowGizmo(true);
            onClose();
        }
    };

    const captureAndUse = async () => {
        const renderer = rendererRef.current;
        if (!renderer) return;
        setSaving(true);
        setError(null);
        // Objects hidden imperatively for the capture (restored in finally).
        const hiddenHelpers: THREE.Object3D[] = [];
        // Free view: the capture is taken from the SHOT camera, not the editor
        // view — the main camera is moved there for the render and restored after.
        let restoreCam: (() => void) | null = null;
        try {
            // Hide the grid + gizmo + bypass the composer, then FORCE a plain
            // render into the preserved buffer (the composer's buffer is
            // unreliable to grab from) so the capture has no grid/controls.
            setCapturing(true);
            await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
            const scene = sceneRef.current;
            const camera = cameraRef.current;
            restoreCam = captureFromShot(camera, focalLength);
            if (scene && camera) {
                // Belt-and-suspenders: the grid/handles unmount on `capturing`, but
                // React's unmount can race this imperative render, so also hide any
                // tagged helper still in the scene graph so it can't leak into the PNG.
                hiddenHelpers.push(...hideCaptureHelpers(scene));
                scene.background = null; // drop the grey backdrop → transparent
                renderer.autoClear = true;
                renderer.setClearColor('#ffffff', 0); // clearAlpha 0
                renderer.clear();
                renderer.render(scene, camera);
            }
            // Look at what was just rendered, before it is uploaded. An empty
            // frame encodes to a perfectly valid PNG and reaches the vendor
            // looking like a successful shot of nothing — which is exactly
            // what happened when the wait ended before the floor was drawn.
            if (!frameHasContent()) {
                throw new Error('The 3D view rendered an empty frame');
            }
            const blob: Blob | null = await new Promise((resolve) => {
                renderer.domElement.toBlob((b) => resolve(b), 'image/png', 0.95);
            });
            if (!blob) throw new Error('Canvas toBlob returned null');
            const file = new File([blob], `surface3d-${Date.now()}.png`, { type: 'image/png' });
            const url = await onCapture(file);
            const placedList = mode === 'applied' ? describeSceneProps(sceneProps) : [];
            onSnapshotUpdated(url, { lensMm: focalLengthRef.current, aperture, aspectRatio: aspectRatioSelRef.current, mode, cameraView: shotCameraViewText(), camera: (() => {
                const v = currentShotView();
                // The framing, whole: where it stands, where it looks, and how
                // much it sees.
                return v ? { ...v, fovDegV: measuredFovRef.current ?? undefined } : v;
            })(), matFloor, matWalls, appliedInset: anyInset, pieces: mode === 'single' ? pieces : undefined, sceneProps: placedList });
            saveSession();
            onClose();
        } catch (err: any) {
            setError(err?.message || 'Failed to capture view');
        } finally {
            restoreCaptureHelpers(hiddenHelpers);
            if (restoreCam) restoreCam(); // put the editor camera back (free-view capture)
            renderer.setClearColor('#CDD1D8', 1); // restore the posing backdrop
            setCapturing(false);
            setSaving(false);
        }
    };

    // ── Bottom dock — every tool lives in the floating action bar; clicking an
    // icon opens its compact options panel above the bar (StudioDock).
    const { t } = useTranslation();
    const tFloor = t('CONTENT_STUDIO.STUDIO_FLOOR', 'Floor');
    const tWallBack = t('CONTENT_STUDIO.STUDIO_WALL_BACK', 'Back wall');
    const tWallLeft = t('CONTENT_STUDIO.STUDIO_WALL_LEFT', 'Left wall');
    const tWallRight = t('CONTENT_STUDIO.STUDIO_WALL_RIGHT', 'Right wall');
    const tBack = t('CONTENT_STUDIO.STUDIO_BACK', 'Back');
    const tLeft = t('CONTENT_STUDIO.STUDIO_LEFT', 'Left');
    const tRight = t('CONTENT_STUDIO.STUDIO_RIGHT', 'Right');
    const tNone = t('CONTENT_STUDIO.STUDIO_NONE', 'None');
    const tArea = t('CONTENT_STUDIO.STUDIO_AREA', 'Area');
    const tFull = t('CONTENT_STUDIO.STUDIO_FULL', 'Full');
    const cladValue = [matFloor && tFloor, matBack && wallBack && tBack, matLeft && wallLeft && tLeft, matRight && wallRight && tRight].filter(Boolean).join(' · ') || tNone;
    const wallsValue = [wallBack && tBack, wallLeft && tLeft, wallRight && tRight].filter(Boolean).join(' · ') || tNone;
    const cladList: Array<{ key: string; label: string; area: boolean; toggle: () => void }> = [];
    if (matFloor) cladList.push({ key: 'floor', label: tFloor, area: areaFloor, toggle: () => toggleArea(areaFloor, setAreaFloor, covFloor, setCovFloor) });
    if (matBack && wallBack) cladList.push({ key: 'back', label: tWallBack, area: areaBack, toggle: () => toggleArea(areaBack, setAreaBack, covBack, setCovBack) });
    if (matLeft && wallLeft) cladList.push({ key: 'left', label: tWallLeft, area: areaLeft, toggle: () => toggleArea(areaLeft, setAreaLeft, covLeft, setCovLeft) });
    if (matRight && wallRight) cladList.push({ key: 'right', label: tWallRight, area: areaRight, toggle: () => toggleArea(areaRight, setAreaRight, covRight, setCovRight) });

    const dockItems: StudioDockItemDef[] = [];
    if (!lockMode) {
        dockItems.push({
            key: 'shot',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_SHOT', 'Shot'),
            value: mode === 'applied' ? t('CONTENT_STUDIO.STUDIO_SHOT_APPLIED', 'Applied') : t('CONTENT_STUDIO.STUDIO_SHOT_SINGLE', 'Single piece'),
            icon: <SwitchCamera />,
            panelWidth: 260,
            panel: (
                <div>
                    <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_DOCK_SHOT', 'Shot')} value={mode === 'applied' ? t('CONTENT_STUDIO.STUDIO_SHOT_APPLIED', 'Applied') : t('CONTENT_STUDIO.STUDIO_SHOT_SINGLE', 'Single piece')} />
                    <div className="grid grid-cols-2 gap-1.5">
                        <StudioPill active={mode === 'single'} onClick={() => setMode('single')}>{t('CONTENT_STUDIO.STUDIO_SHOT_SINGLE', 'Single piece')}</StudioPill>
                        <StudioPill active={mode === 'applied'} onClick={() => setMode('applied')}>{t('CONTENT_STUDIO.STUDIO_SHOT_APPLIED', 'Applied')}</StudioPill>
                    </div>
                </div>
            ),
        });
    }
    if (mode === 'applied') {
        dockItems.push({
            key: 'material',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_MATERIAL', 'Material'),
            value: cladValue,
            icon: <Layers />,
            panelWidth: 320,
            panel: (
                <>
                    <div>
                        <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_APPLY_TO', 'Apply material to')} value={cladValue} />
                        <div className="flex flex-wrap gap-1.5">
                            {/* Multi-select — floor and/or any individual (present) wall. Keep
                                at least one selected. */}
                            {(() => {
                                const active = (matFloor ? 1 : 0) + (matBack && wallBack ? 1 : 0) + (matLeft && wallLeft ? 1 : 0) + (matRight && wallRight ? 1 : 0);
                                const tog = (on: boolean, set: React.Dispatch<React.SetStateAction<boolean>>) => { if (on && active <= 1) return; set((v) => !v); };
                                return (
                                    <>
                                        {allowsFloor && <StudioPill active={matFloor} onClick={() => tog(matFloor, setMatFloor)}>{tFloor}</StudioPill>}
                                        {allowsWalls && wallBack && <StudioPill active={matBack} onClick={() => tog(matBack, setMatBack)}>{tWallBack}</StudioPill>}
                                        {allowsWalls && wallLeft && <StudioPill active={matLeft} onClick={() => tog(matLeft, setMatLeft)}>{tWallLeft}</StudioPill>}
                                        {allowsWalls && wallRight && <StudioPill active={matRight} onClick={() => tog(matRight, setMatRight)}>{tWallRight}</StudioPill>}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                    {/* COVERAGE — whole face, or a dragged sub-area (backsplash band,
                        floor inset). Area surfaces show green corner handles in-scene. */}
                    {cladList.length > 0 && (
                        <div className="mt-4">
                            <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_COVERAGE', 'Coverage')} value={anyInset ? tArea : t('CONTENT_STUDIO.STUDIO_FULL_SURFACE', 'Full surface')} />
                            <div className="flex flex-wrap gap-1.5">
                                {cladList.map((c) => (
                                    <StudioPill key={c.key} active={c.area} onClick={c.toggle}>{c.label}: {c.area ? tArea : tFull}</StudioPill>
                                ))}
                            </div>
                            {anyInset && (
                                <div className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
                                    {t('CONTENT_STUDIO.STUDIO_COVERAGE_HINT', 'Drag a corner to resize each area, or drag its centre to move it. The rest of the surface becomes the environment.')}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="mt-4">
                        <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_TILE_SIZE', 'Tile size')} value={tileScale === 1 ? t('CONTENT_STUDIO.STUDIO_REAL', 'Real') : `${tileScale}×`} />
                        <StudioMarkSlider
                            value={Math.max(0, TILE_SCALES.indexOf(tileScale))}
                            min={0}
                            max={TILE_SCALES.length - 1}
                            discrete
                            marks={TILE_SCALES.map((_, i) => ({ value: i }))}
                            onChange={(i) => setTileScale(TILE_SCALES[i])}
                        />
                    </div>
                </>
            ),
        });
        dockItems.push({
            key: 'room',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_ROOM', 'Room'),
            value: wallsValue,
            icon: <BoxIcon />,
            panelWidth: 280,
            panel: (
                <>
                    <div>
                        <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_WALLS', 'Walls')} value={wallsValue} />
                        <div className="grid grid-cols-3 gap-1.5">
                            <StudioPill active={wallBack} onClick={() => setWallBack((v) => !v)}>{tBack}</StudioPill>
                            <StudioPill active={wallLeft} onClick={() => setWallLeft((v) => !v)}>{tLeft}</StudioPill>
                            <StudioPill active={wallRight} onClick={() => setWallRight((v) => !v)}>{tRight}</StudioPill>
                        </div>
                    </div>
                    <div className="mt-4">
                        <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_SIZE', 'Size')} value={t('CONTENT_STUDIO.STUDIO_DRAG_EDGES', 'Drag edges')} />
                        <div className="text-[11px] leading-snug text-muted-foreground">
                            {t('CONTENT_STUDIO.STUDIO_SIZE_HINT', 'Drag the arrows on the floor / wall edges in the scene to resize the room.')}
                        </div>
                    </div>
                </>
            ),
        });
    } else {
        dockItems.push({
            key: 'pieces',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_PIECES', 'Pieces'),
            value: `${pieces}`,
            icon: <Copy />,
            panelWidth: 240,
            panel: (
                <div>
                    <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_DOCK_PIECES', 'Pieces')} value={`${pieces}`} />
                    <div className="grid grid-cols-2 gap-1.5">
                        <StudioPill active={false} onClick={() => setPieces((p) => Math.max(1, p - 1))}>{t('CONTENT_STUDIO.STUDIO_REMOVE', '− Remove')}</StudioPill>
                        <StudioPill active={false} onClick={() => setPieces((p) => Math.min(12, p + 1))}>{t('CONTENT_STUDIO.STUDIO_ADD', '+ Add')}</StudioPill>
                    </div>
                </div>
            ),
        });
        dockItems.push({
            key: 'thickness',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_THICKNESS', 'Thickness'),
            value: `${thicknessMm} mm`,
            icon: <Ruler />,
            panelWidth: 300,
            panel: (
                <div>
                    <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_DOCK_THICKNESS', 'Thickness')} value={`${thicknessMm} mm`} />
                    <div className="mb-1 grid grid-cols-4 gap-1.5">
                        {[6, 10, 12, 20].map((mm) => (
                            <StudioPill key={mm} active={thicknessMm === mm} onClick={() => setThicknessMm(mm)}>{mm}</StudioPill>
                        ))}
                    </div>
                    <StudioMarkSlider value={thicknessMm} min={2} max={40} onChange={(v) => setThicknessMm(Math.round(v))} marks={[6, 10, 12, 20].map((v) => ({ value: v }))} />
                </div>
            ),
        });
    }
    dockItems.push({
        key: 'frame',
        label: t('CONTENT_STUDIO.STUDIO_DOCK_FRAME', 'Frame'),
        value: aspectRatioSel,
        icon: <Ratio />,
        panelWidth: 260,
        panel: (
            <div>
                <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_DOCK_FRAME', 'Frame')} value={aspectRatioSel} />
                <div className="grid grid-cols-3 gap-1.5">
                    {ASPECT_PRESETS.map((a) => (
                        <StudioPill key={a.v} active={aspectRatioSel === a.v} onClick={() => { setAspectRatioSel(a.v); onAspectRatioChange?.(a.v); }}>{a.label}</StudioPill>
                    ))}
                </div>
            </div>
        ),
    });
    dockItems.push({
        key: 'lens',
        label: t('CONTENT_STUDIO.STUDIO_DOCK_LENS', 'Lens'),
        value: `${focalLength}mm`,
        icon: <Camera />,
        panelWidth: 320,
        panel: (
            <div>
                <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_DOCK_LENS', 'Lens')} value={`${focalLength}mm · ${lensCharacter(focalLength)}`} />
                <div className="mb-1 grid grid-cols-3 gap-1.5">
                    {LENS_PRESETS.map((p) => (
                        <StudioPill key={p.mm} active={focalLength === p.mm} onClick={() => takeLens(p.mm)}>{p.mm}mm</StudioPill>
                    ))}
                </div>
                <StudioMarkSlider value={focalLength} min={14} max={200} onChange={takeLens} marks={LENS_PRESETS.map((p) => ({ value: p.mm }))} />
            </div>
        ),
    });
    dockItems.push({
        key: 'depth',
        label: t('CONTENT_STUDIO.STUDIO_DOCK_DEPTH', 'Depth'),
        value: `f/${aperture}`,
        icon: <Aperture />,
        panelWidth: 320,
        panel: (
            <div>
                <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_DOCK_DEPTH', 'Depth of field')} value={`f/${aperture}`} />
                <div className="mb-1 grid grid-cols-4 gap-1.5">
                    {APERTURE_STOPS.map((f) => (
                        <StudioPill key={f} active={aperture === f} onClick={() => setAperture(f)}>f/{f}</StudioPill>
                    ))}
                </div>
                <StudioMarkSlider
                    value={Math.max(0, APERTURE_STOPS.indexOf(aperture))}
                    min={0}
                    max={APERTURE_STOPS.length - 1}
                    discrete
                    marks={APERTURE_STOPS.map((_, i) => ({ value: i }))}
                    onChange={(i) => setAperture(APERTURE_STOPS[i])}
                />
                <p className="text-xs leading-normal text-muted-foreground">
                    {apertureCharacter(aperture)}.
                </p>
            </div>
        ),
    });
    if (mode === 'single') dockItems.push(...takeDockItems(t, takes, { onTakeRecorded, onClose: handleClose }));

    return (
        <Dialog
            open={open}
            // handleClose, not onClose: dismissing by Escape or a backdrop click
            // is still finishing with the studio, and it went straight out
            // without saving. Only the explicit X saved, so a shot framed and
            // then dismissed came back to Re-frame having forgotten it. The
            // furniture studio next door already closed through its own
            // handler; this one had drifted.
            onOpenChange={(o) => { if (!o) handleClose(); }}
        >
            <DialogContent
                showCloseButton={false}
                className="block w-[calc(100%-2rem)] max-w-[1040px] gap-0 overflow-hidden p-0 sm:max-w-[1040px]"
            >
                <DialogTitle className="sr-only">{t('CONTENT_STUDIO.STUDIO_SURFACE_TITLE', 'Room studio')}</DialogTitle>
            <div
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onWheel={(e) => e.stopPropagation()}
                // Right-click bubbles through the portal to the canvas behind,
                // which would open the canvas context menu over the studio.
                onContextMenu={(e) => { e.stopPropagation(); e.preventDefault(); }}
                className="relative flex h-[78vh] max-h-[720px] bg-muted"
            >

                {/* Left rail — what BUILDS the scene, kept apart from the camera
                    settings in the dock below. Same arrangement as the furniture
                    studio: everything here is a place to put something. */}
                <div className="z-[7] flex w-[68px] shrink-0 flex-col items-center gap-1 border-e bg-background py-3">
                    <StudioRailButton
                        active={leftPanel === 'furniture'}
                        onClick={() => setLeftPanel((p) => (p === 'furniture' ? null : 'furniture'))}
                        icon={<Armchair />}
                        label={t('CONTENT_STUDIO.STUDIO_DOCK_PROPS', 'Props')}
                        badge={sceneProps.length || undefined}
                    />
                </div>

                {/* Left panel — the library, two columns, beside the scene rather
                    than in a popover over it: placing furniture is a
                    browse-and-drag job, not a menu pick. */}
                {leftPanel === 'furniture' && (
                    <div className="z-[7] flex w-[292px] shrink-0 flex-col border-e bg-background">
                        <div className="flex items-center justify-between border-b px-4 py-3">
                            <span className="text-[13px] font-medium text-foreground">
                                {t('CONTENT_STUDIO.STUDIO_PROPS', 'Furniture library')}
                            </span>
                            <button
                                onClick={() => setLeftPanel(null)}
                                aria-label={t('CONTENT_STUDIO.STUDIO_CLOSE', 'Close')}
                                className="cursor-pointer border-0 bg-transparent leading-none text-muted-foreground hover:text-foreground"
                            >
                                <X className="size-[18px]" />
                            </button>
                        </div>
                        <StudioPropLibraryPanel library={propLibrary} onPlace={placeProp} />
                        {sceneProps.length > 0 && (
                            <div className="max-h-[168px] overflow-y-auto border-t p-3">
                                <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_PROPS_PLACED', 'In the scene')} value={`${sceneProps.length}`} />
                                {sceneProps.map((p) => (
                                    <div key={p.id} className="flex items-center gap-1.5 py-1">
                                        <button
                                            onClick={() => { setSelPropId(p.id); const g = propRefs.current.get(p.id); if (g) setSelPropObj(g); }}
                                            className={cn(
                                                'min-w-0 flex-1 cursor-pointer truncate border-0 bg-transparent p-0 text-start text-xs',
                                                selPropId === p.id ? 'font-medium text-foreground' : 'text-foreground',
                                            )}
                                        >
                                            {p.name}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => deleteProp(p.id)}
                                            title={t('CONTENT_STUDIO.STUDIO_REMOVE', 'Remove') as string}
                                            className="cursor-pointer border-0 bg-transparent p-0 leading-none text-muted-foreground hover:text-destructive"
                                        >
                                            <Trash2 className="size-[15px]" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Scene column */}
                <div className="relative min-w-0 flex-1">
                {/* Full-bleed viewport — the frame floats centred, clear of the dock.
                    containerType:size lets the frame size itself against BOTH the
                    available width and height (cqw/cqh) so any aspect ratio is
                    letterbox-contained correctly. */}
                <div className="absolute inset-0 flex items-center justify-center px-4 pt-4 pb-24 [container-type:size]">
                    <div
                        onWheel={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="relative max-h-full max-w-full overflow-hidden bg-muted"
                        // Fit within both dimensions: width is the smaller of the full
                        // container width and (container height × aspect), so a square
                        // stays square in a landscape container instead of overflowing.
                        style={{ aspectRatio: `${aspect}`, width: `min(100cqw, calc(100cqh * ${aspect}))` }}
                    >
                        <Canvas
                            dpr={[2, 3]}
                            shadows
                            // alpha so the capture can be a transparent cutout (the
                            // piece / room only) instead of the grey posing backdrop.
                            gl={{ preserveDrawingBuffer: true, antialias: true, alpha: true }}
                            camera={{ position: [2.6, 2.0, 3.4], fov: 35 }}
                            onCreated={({ gl, scene, camera }) => {
                                rendererRef.current = gl;
                                sceneRef.current = scene;
                                cameraRef.current = camera;
                                gl.setClearColor('#CDD1D8', 1);
                                // ACES filmic tone mapping (drei's default) compresses
                                // saturation → the captured material looked washed out.
                                // Use neutral tone mapping so the texture's colour is
                                // reproduced faithfully in the capture.
                                gl.toneMapping = THREE.NoToneMapping;
                                gl.toneMappingExposure = 1.0;
                            }}
                        >
                            {/* Grey posing backdrop; dropped during capture so the
                                frame is transparent (not the grey background). */}
                            {!capturing && <color attach="background" args={['#CDD1D8']} />}
                            <CameraRig focalLength={camMode === 'edit' ? EDITOR_FOCAL_LENGTH : focalLength} />
                            <HoldMeasuredFov fovRef={measuredFovRef} active={camMode === 'shot'} />
                            <CameraPathAnimator jobRef={takes.camPathJobRef} />
                            <ReleaseContextOnUnmount />
                            <RestoreCameraSurface savedRef={savedCameraRef} controlsRef={controlsRef} />
                            <ambientLight intensity={0.3} />
                            <directionalLight
                                position={[4, 7, 3]}
                                intensity={1.4}
                                castShadow
                                shadow-mapSize-width={2048}
                                shadow-mapSize-height={2048}
                                shadow-camera-near={0.5}
                                shadow-camera-far={24}
                                shadow-camera-left={-6}
                                shadow-camera-right={6}
                                shadow-camera-top={6}
                                shadow-camera-bottom={-6}
                                shadow-bias={-0.0004}
                                shadow-normalBias={0.02}
                            />
                            <directionalLight position={[-5, 3, -2]} intensity={0.3} />
                            <Suspense fallback={null}>
                                <Environment resolution={256} frames={1}>
                                    <Lightformer form="rect" intensity={0.7} position={[0, 4, 3]} rotation={[-Math.PI / 3, 0, 0]} scale={[9, 5, 1]} />
                                    <Lightformer form="rect" intensity={0.35} position={[-5, 2, 1]} rotation={[0, Math.PI / 4, 0]} scale={[4, 6, 1]} />
                                    <Lightformer form="rect" intensity={0.35} position={[5, 2, 1]} rotation={[0, -Math.PI / 4, 0]} scale={[4, 6, 1]} />
                                </Environment>
                                {mode === 'applied'
                                    ? (resolvedTexture && <RoomCornerScene textureUrl={resolvedTexture} matFloor={matFloor} matBack={matBack} matLeft={matLeft} matRight={matRight} tileW={(seamlessW || tileW) * tileScale} tileH={(seamlessH || tileH) * tileScale} gridBearingDeg={gridBearing} floorW={floorW} floorD={floorD} wallH={wallH} onResize={(w, d, h) => { setFloorW(w); setFloorD(d); setWallH(h); }} onDragActive={(a) => { if (controlsRef.current) controlsRef.current.enabled = !a; }} showHandles={!capturing} walls={{ back: wallBack, left: wallLeft, right: wallRight }} area={{ floor: areaFloor, back: areaBack, left: areaLeft, right: areaRight }} cov={{ floor: covFloor, back: covBack, left: covLeft, right: covRight }} onCov={(s, c) => { if (s === 'floor') setCovFloor(c); else if (s === 'back') setCovBack(c); else if (s === 'left') setCovLeft(c); else setCovRight(c); }} onToggleSurface={toggleSurfaceMat} />)
                                    : (materialPaths.length > 0 && <SinglePieceScene key={`${layout}-${pieces}-${poseNonce}`} groundColor={recording ? (headlessClip?.backdrop || STUDIO_WHITE) : undefined} textures={materialPaths} count={pieces} layout={layout} tileW={tileW} tileH={tileH} thickness={thicknessMm / 1000} onSelect={setSelectedObj} capturing={capturing} savedTransforms={s0.pieceTransforms} registerPiece={registerPiece} />)}
                                {showGrid && !capturing && (
                                    <Grid userData={{ studioHelper: true }} position={[0, 0.001, 0]} infiniteGrid cellSize={0.5} cellThickness={0.6} cellColor="#9096A0" sectionSize={2.5} sectionThickness={1} sectionColor="#6E7480" fadeDistance={22} followCamera={false} />
                                )}
                            </Suspense>
                            {mode === 'single' && showGizmo && !capturing && selectedObj && (
                                <TransformControls object={selectedObj} mode={transformMode} size={0.7} />
                            )}
                            {/* Real library furniture standing in the built room.
                                Captured as itself, so the reskin lights it rather
                                than inventing something in its place. */}
                            {mode === 'applied' && sceneProps.map((p) => (
                                <ScenePropObject
                                    key={p.id}
                                    prop={p}
                                    selected={selPropId === p.id}
                                    capturing={capturing}
                                    groupRef={(g) => {
                                        if (g) { propRefs.current.set(p.id, g); if (p.id === selPropId) setSelPropObj(g); }
                                        else propRefs.current.delete(p.id);
                                    }}
                                    onSelect={(obj) => { setSelPropId(p.id); setSelPropObj(obj); }}
                                />
                            ))}
                            {mode === 'applied' && !capturing && selPropObj && (
                                <>
                                    <TransformControls object={selPropObj} mode={propGizmo} size={0.7} onMouseUp={commitPropTransform} />
                                    {/* Always available, whichever mode the gizmo is in. */}
                                    <UniformScaleHandle object={selPropObj} onCommit={commitPropTransform} controlsRef={controlsRef} />
                                </>
                            )}
                            {mode === 'applied' && <FloorDropResolver resolverRef={floorDropRef} />}
                            {/* Phase 3 — free view: the shot camera as a scene object
                                (click to look through it) + a live PiP of its view.
                                Suppressed while a placed piece is selected so the two
                                gizmos never fight. */}
                            {camMode === 'edit' && !capturing && !selPropId && shotViewRef.current && (
                                <CameraObject view={shotViewRef.current} focalLength={focalLength} aspect={aspect} onEnter={toggleCamMode} editable gizmoMode={camGizmoMode} onViewChange={(v) => { shotViewRef.current = v; }} />
                            )}
                            {camMode === 'edit' && !capturing && selPropId && shotViewRef.current && (
                                <CameraObject view={shotViewRef.current} focalLength={focalLength} aspect={aspect} onEnter={toggleCamMode} />
                            )}
                            {camMode === 'edit' && !capturing && (
                                <PiPView viewRef={shotViewRef} focalLength={focalLength} />
                            )}
                            <OrbitControls
                                ref={controlsRef}
                                makeDefault
                                target={[0, 0.4, 0]}
                                enablePan
                                enableZoom
                                enableDamping
                                dampingFactor={0.1}
                                minDistance={1.2}
                                maxDistance={14}
                                maxPolarAngle={Math.PI / 2.02}
                            />
                            {!capturing && (
                                <EffectComposer multisampling={4}>
                                    <DepthOfField target={[focusTarget.x, focusTarget.y, focusTarget.z]} worldFocusRange={dof.worldFocusRange} bokehScale={dof.bokehScale} />
                                    <BrightnessContrast brightness={-0.02} contrast={0.18} />
                                </EffectComposer>
                            )}
                        </Canvas>

                        {/* Top-left gizmo toolbar — Move / Rotate + Deselect (single). */}
                        {mode === 'single' && showGizmo && !capturing && (
                            <div className={cn(STUDIO_TOOLBAR_CLASS, 'top-2.5 left-2.5')}>
                                <StudioIconToggle active={transformMode === 'translate'} onClick={() => setTransformMode('translate')} label={t('CONTENT_STUDIO.STUDIO_MOVE', 'Move')}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20M12 2l-3 3M12 2l3 3M12 22l-3-3M12 22l3-3M2 12l3-3M2 12l3 3M22 12l-3-3M22 12l-3 3" /></svg>
                                </StudioIconToggle>
                                <StudioIconToggle active={transformMode === 'rotate'} onClick={() => setTransformMode('rotate')} label={t('CONTENT_STUDIO.STUDIO_ROTATE', 'Rotate')}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" /></svg>
                                </StudioIconToggle>
                                <StudioToolbarDivider />
                                <StudioIconToggle active={false} onClick={() => setSelectedObj(null)} label={t('CONTENT_STUDIO.STUDIO_DESELECT', 'Deselect')}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                                </StudioIconToggle>
                            </div>
                        )}
                        {/* Free view — camera pose gizmo (move / rotate) top-left.
                            Hidden while a placed piece is selected (its toolbar takes over). */}
                        {camMode === 'edit' && !capturing && !selPropId && (
                            <div className={cn(STUDIO_TOOLBAR_CLASS, 'top-2.5 left-2.5')}>
                                <StudioIconToggle active={camGizmoMode === 'translate'} onClick={() => setCamGizmoMode('translate')} label={t('CONTENT_STUDIO.STUDIO_MOVE_CAMERA', 'Move camera')}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20M12 2l-3 3M12 2l3 3M12 22l-3-3M12 22l3-3M2 12l3-3M2 12l3 3M22 12l-3-3M22 12l-3 3" /></svg>
                                </StudioIconToggle>
                                <StudioIconToggle active={camGizmoMode === 'rotate'} onClick={() => setCamGizmoMode('rotate')} label={t('CONTENT_STUDIO.STUDIO_ROTATE_CAMERA', 'Rotate camera')}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" /></svg>
                                </StudioIconToggle>
                            </div>
                        )}
                        {/* Selected piece — move / rotate + delete. Scale is not here:
                            it is the blue handle on the piece itself, so it needs no
                            mode, and a real mesh is only ever scaled uniformly. */}
                        {mode === 'applied' && selPropId && !capturing && (
                            <div className={cn(STUDIO_TOOLBAR_CLASS, 'top-2.5 left-2.5')}>
                                <StudioIconToggle active={propGizmo === 'translate'} onClick={() => setPropGizmo('translate')} label={t('CONTENT_STUDIO.STUDIO_MOVE', 'Move')}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20M12 2l-3 3M12 2l3 3M12 22l-3-3M12 22l3-3M2 12l3-3M2 12l3 3M22 12l-3-3M22 12l-3 3" /></svg>
                                </StudioIconToggle>
                                <StudioIconToggle active={propGizmo === 'rotate'} onClick={() => setPropGizmo('rotate')} label={t('CONTENT_STUDIO.STUDIO_ROTATE', 'Rotate')}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" /></svg>
                                </StudioIconToggle>
                                <StudioToolbarDivider />
                                <StudioIconToggle active={false} onClick={() => deleteProp(selPropId)} label={t('CONTENT_STUDIO.STUDIO_DELETE', 'Delete')}>
                                    <Trash2 />
                                </StudioIconToggle>
                            </div>
                        )}
                        {/* Top-right view toggles — camera/free view + grid + gizmo. */}
                        {!capturing && (
                            <div className={cn(STUDIO_TOOLBAR_CLASS, 'top-2.5 right-2.5')}>
                                <StudioIconToggle
                                    active={camMode === 'shot'}
                                    onClick={toggleCamMode}
                                    label={camMode === 'shot' ? t('CONTENT_STUDIO.STUDIO_VIEW_FREE', 'Free view') : t('CONTENT_STUDIO.STUDIO_VIEW_SHOT', 'Look through camera')}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" /></svg>
                                </StudioIconToggle>
                                <StudioToolbarDivider />
                                <StudioIconToggle active={showGrid} onClick={() => setShowGrid((v) => !v)} label={showGrid ? 'Hide grid' : 'Show grid'}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="1.5" /><path d="M9 3v18M15 3v18M3 9h18M3 15h18" /></svg>
                                </StudioIconToggle>
                                {mode === 'single' && (
                                    <StudioIconToggle active={showGizmo} onClick={() => setShowGizmo((v) => !v)} label={showGizmo ? 'Hide controls' : 'Show controls'}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></svg>
                                    </StudioIconToggle>
                                )}
                            </div>
                        )}

                        {/* PiP frame — outlines the live camera preview (bottom-left of
                            the canvas, rendered by PiPView); click to look through. */}
                        {camMode === 'edit' && !capturing && (
                            <div
                                onClick={toggleCamMode}
                                title={t('CONTENT_STUDIO.STUDIO_VIEW_SHOT', 'Look through camera')}
                                className="absolute bottom-3 left-3 z-[2] cursor-pointer rounded-lg border border-foreground/55 transition-colors hover:border-foreground"
                                style={{ width: 190, aspectRatio: `${aspect}` }}
                            >
                                <div className="pointer-events-none absolute -top-px -left-px bg-foreground/85 px-1.5 py-0.5 text-[10px] font-medium text-background">
                                    {t('CONTENT_STUDIO.STUDIO_PIP_CAMERA', 'Camera')}
                                </div>
                            </div>
                        )}

                        <div className="pointer-events-none absolute right-0 bottom-3 left-0 text-center">
                            <span className="inline-block max-w-[70%] bg-background/80 px-2 py-0.5 text-[11px] text-muted-foreground">
                                {camMode === 'edit'
                                    ? t('CONTENT_STUDIO.STUDIO_HINT_FREE', 'Free view — your shot stays framed. Drag the gizmo to pose the camera, or click it / the preview to look through it.')
                                    : mode === 'single' ? 'Click a piece to select · drag the gizmo to move / rotate' : 'Drag to orbit · scroll to zoom · right-drag to pan'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Close — floating over the viewport. */}
                <button
                    onClick={handleClose}
                    aria-label={t('CONTENT_STUDIO.STUDIO_CLOSE', 'Close')}
                    className="absolute top-3 right-3 z-[6] flex size-8 cursor-pointer items-center justify-center rounded-full border bg-background/90 text-muted-foreground backdrop-blur-md transition-colors hover:bg-background hover:text-foreground"
                >
                    <X className="size-[18px]" />
                </button>

                {/* Error chip — floats just above the dock. */}
                {error && (
                    <div className="absolute bottom-[88px] left-1/2 z-[6] max-w-[80%] -translate-x-1/2 rounded-lg border border-destructive/30 bg-background/95 px-3 py-1 text-xs text-destructive">
                        {error}
                    </div>
                )}

                {/* Bottom dock — all tools + the Capture action. */}
                <StudioDock
                    items={dockItems}
                    initialOpenKey={initialDock}
                    action={(
                        <Button
                            onClick={captureAndUse}
                            disabled={saving}
                            variant="outline"
                            className="h-auto gap-1.5 bg-background/90 px-4 py-2.5 text-sm whitespace-nowrap backdrop-blur-xl"
                        >
                            <Scan className="size-[15px]" />
                            {saving ? t('CONTENT_STUDIO.STUDIO_CAPTURING', 'Capturing…') : t('CONTENT_STUDIO.STUDIO_CAPTURE', 'Capture view')}
                        </Button>
                    )}
                />
                </div>
            </div>
            </DialogContent>
        </Dialog>
    );
};

// ─── Scenes ─────────────────────────────────────────────────────────────

/**
 * Keep the camera at the field of view a reference was measured at.
 *
 * Everything else that touches the lens does so through millimetres, and
 * millimetres only mean a field of view once the aspect ratio is fixed. The
 * studio's aspect is not fixed — the recipe sets it, the panel resizes — so a
 * value that was correct when written became a wider shot a frame later. An
 * angle re-asserted every frame cannot drift, whatever else runs.
 *
 * Only in shot mode: the editor deliberately uses its own wide lens.
 */
const HoldMeasuredFov: React.FC<{
    fovRef: React.MutableRefObject<number | null>;
    active: boolean;
}> = ({ fovRef, active }) => {
    const camera = useThree((s) => s.camera) as THREE.PerspectiveCamera;
    useFrame(() => {
        const want = fovRef.current;
        if (!active || !want || !(camera as any).isPerspectiveCamera) return;
        if (Math.abs(camera.fov - want) > 0.01) {
            camera.fov = want;
            camera.updateProjectionMatrix();
        }
    });
    return null;
};

/** Restore a saved camera position/target on (re)mount so Re-frame keeps the
 *  vendor's last framing. */
const RestoreCameraSurface: React.FC<{
    savedRef: React.MutableRefObject<{ pos: [number, number, number]; target: [number, number, number] } | null>;
    controlsRef: React.MutableRefObject<any>;
}> = ({ savedRef, controlsRef }) => {
    const camera = useThree((s) => s.camera);
    useEffect(() => {
        const saved = savedRef.current;
        if (!saved) return;
        let raf = 0;
        let tries = 0;
        // OrbitControls may not be mounted on the first frame — retry until it is,
        // otherwise the camera position sticks but the target/zoom doesn't, so a
        // correctly-sized room looks framed at the wrong scale after re-framing.
        const apply = () => {
            camera.position.set(saved.pos[0], saved.pos[1], saved.pos[2]);
            const c = controlsRef.current;
            if (c) {
                c.target.set(saved.target[0], saved.target[1], saved.target[2]);
                c.update();
                return;
            }
            if (tries++ < 30) raf = requestAnimationFrame(apply);
        };
        apply();
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
};

function useSurfaceTexture(url: string, repeatX: number, repeatY: number, rotationDeg = 0) {
    const base = useTexture(url) as THREE.Texture;
    return useMemo(() => {
        // CLONE per use — drei caches one texture instance per URL, so floor + walls
        // would otherwise share (and clobber) each other's `.repeat`. Each mesh gets
        // its own clone with its own repeat.
        const tex = base.clone();
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(repeatX, repeatY);
        if (rotationDeg) {
            // How the reference's floor is actually LAID.
            //
            // A camera can be exactly right over a grid laid the wrong way and
            // the joints will still run somewhere the reference's do not — the
            // one thing a vendor notices immediately. Rotated about the middle
            // of the tile, so turning it does not also slide the pattern.
            tex.center.set(0.5, 0.5);
            tex.rotation = THREE.MathUtils.degToRad(rotationDeg);
        }
        tex.anisotropy = 16;
        // Colour maps must be decoded from sRGB, otherwise they render washed-out.
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.needsUpdate = true;
        return tex;
    }, [base, repeatX, repeatY, rotationDeg]);
}

/** Room corner: floor + the walls the vendor enabled (back / left / right).
 *  The chosen surface (floor OR walls) carries the seamless material at its real
 *  coverage; the OTHER surfaces are plain neutral placeholders. The WHOLE built
 *  structure is kept in the capture — the worker repaints the neutral placeholder
 *  surfaces into the environment while preserving the geometry, camera + material. */
/** A double-headed axis arrow gizmo (like a real 3D tool's resize handle).
 *  Dragging moves along one world axis (x / z on the floor plane, y for wall
 *  height); the parent maps that coordinate to a size. An invisible-but-larger
 *  hit sphere makes it easy to grab. */
const AxisArrow: React.FC<{
    position: [number, number, number];
    axis: 'x' | 'z' | 'y';
    onDrag: (coord: number) => void;
    onDragActive: (active: boolean) => void;
}> = ({ position, axis, onDrag, onDragActive }) => {
    const { camera, gl } = useThree();
    const [hover, setHover] = useState(false);
    const onDown = (e: any) => {
        e.stopPropagation();
        onDragActive(true);
        const rect = gl.domElement.getBoundingClientRect();
        const raycaster = new THREE.Raycaster();
        const ndc = new THREE.Vector2();
        let plane: THREE.Plane;
        if (axis === 'y') {
            // Vertical plane through the handle, facing the camera (drag up/down).
            const n = new THREE.Vector3();
            camera.getWorldDirection(n); n.y = 0;
            if (n.lengthSq() < 1e-4) n.set(0, 0, 1);
            n.normalize();
            plane = new THREE.Plane().setFromNormalAndCoplanarPoint(n, new THREE.Vector3(position[0], position[1], position[2]));
        } else {
            plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0); // y = 0 floor plane
        }
        const hit = new THREE.Vector3();
        const move = (ev: PointerEvent) => {
            ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
            ndc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
            raycaster.setFromCamera(ndc, camera);
            if (raycaster.ray.intersectPlane(plane, hit)) {
                onDrag(axis === 'x' ? hit.x : axis === 'z' ? hit.z : hit.y);
            }
        };
        const up = () => {
            onDragActive(false);
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    };
    // Arrow built along +X, then the group is rotated to face its axis.
    const rot: [number, number, number] =
        axis === 'x' ? [0, 0, 0] : axis === 'z' ? [0, Math.PI / 2, 0] : [0, 0, Math.PI / 2];
    // Neutral graphite — elegant, not a toy blue. Darkens on hover for feedback.
    const color = hover ? '#111827' : '#4B5563';
    const matProps = { color, depthTest: false, transparent: true, opacity: 0.95 } as const;
    return (
        <group userData={{ studioHelper: true }} position={position} rotation={rot}>
            {/* Oversized invisible hit target (kept "visible" with opacity 0 so it raycasts). */}
            <mesh
                onPointerDown={onDown}
                onPointerOver={(e: any) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'grab'; }}
                onPointerOut={() => { setHover(false); document.body.style.cursor = ''; }}
            >
                <sphereGeometry args={[0.26, 12, 12]} />
                <meshBasicMaterial transparent opacity={0} depthTest={false} depthWrite={false} />
            </mesh>
            {/* Shaft + two heads = double-headed arrow. */}
            <mesh rotation={[0, 0, Math.PI / 2]} renderOrder={10}>
                <cylinderGeometry args={[hover ? 0.035 : 0.028, hover ? 0.035 : 0.028, 0.4, 12]} />
                <meshBasicMaterial {...matProps} />
            </mesh>
            <mesh position={[0.2, 0, 0]} rotation={[0, 0, -Math.PI / 2]} renderOrder={10}>
                <coneGeometry args={[hover ? 0.085 : 0.07, 0.16, 14]} />
                <meshBasicMaterial {...matProps} />
            </mesh>
            <mesh position={[-0.2, 0, 0]} rotation={[0, 0, Math.PI / 2]} renderOrder={10}>
                <coneGeometry args={[hover ? 0.085 : 0.07, 0.16, 14]} />
                <meshBasicMaterial {...matProps} />
            </mesh>
        </group>
    );
};

/** A draggable panel-corner handle. Raycasts onto the surface's own plane
 *  (floor / wall, any orientation) and reports the world hit so the parent can
 *  map it to a normalised (u,v) corner of the material area. */
const PanelHandle: React.FC<{
    position: [number, number, number];
    plane: THREE.Plane;
    onDrag: (hit: THREE.Vector3) => void;
    onDragActive: (active: boolean) => void;
}> = ({ position, plane, onDrag, onDragActive }) => {
    const { camera, gl } = useThree();
    const [hover, setHover] = useState(false);
    const onDown = (e: any) => {
        e.stopPropagation();
        onDragActive(true);
        const rect = gl.domElement.getBoundingClientRect();
        const raycaster = new THREE.Raycaster();
        const ndc = new THREE.Vector2();
        const hit = new THREE.Vector3();
        const move = (ev: PointerEvent) => {
            ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
            ndc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
            raycaster.setFromCamera(ndc, camera);
            if (raycaster.ray.intersectPlane(plane, hit)) onDrag(hit.clone());
        };
        const up = () => {
            onDragActive(false);
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    };
    return (
        <mesh userData={{ studioHelper: true }} position={position} onPointerDown={onDown} onPointerOver={() => setHover(true)} onPointerOut={() => setHover(false)}>
            <boxGeometry args={hover ? [0.17, 0.17, 0.17] : [0.14, 0.14, 0.14]} />
            <meshBasicMaterial color={hover ? '#111827' : '#4B5563'} depthTest={false} transparent opacity={0.95} />
        </mesh>
    );
};

const RoomCornerScene: React.FC<{
    textureUrl: string; matFloor?: boolean; matBack?: boolean; matLeft?: boolean; matRight?: boolean; tileW: number; tileH: number;
    /** Bearing of the reference floor's grid, degrees. 0 = laid square to the room. */
    gridBearingDeg?: number;
    floorW?: number; floorD?: number; wallH?: number;
    onResize?: (w: number, d: number, h: number) => void;
    onDragActive?: (active: boolean) => void;
    showHandles?: boolean;
    walls?: { back: boolean; left: boolean; right: boolean };
    area?: { floor: boolean; back: boolean; left: boolean; right: boolean };
    cov?: { floor: Coverage; back: Coverage; left: Coverage; right: Coverage };
    onCov?: (surface: SurfaceKey, cov: Coverage) => void;
    /** Click a surface in the viewport to clad / unclad it directly. */
    onToggleSurface?: (surface: SurfaceKey) => void;
}> = ({ textureUrl, matFloor = true, matBack = false, matLeft = false, matRight = false, tileW, tileH, gridBearingDeg = 0, floorW = 6, floorD = 6, wallH = 3, onResize, onDragActive, showHandles, walls, area, cov, onCov, onToggleSurface }) => {
    const W = floorW, D = floorD, H = wallH;
    const rep = (span: number, tile: number) => Math.max(1, Math.round(span / Math.max(0.05, tile)));
    const aF = area?.floor ?? false, aB = area?.back ?? false, aL = area?.left ?? false, aR = area?.right ?? false;
    const cF = cov?.floor ?? FULL_COV, cB = cov?.back ?? FULL_COV, cL = cov?.left ?? FULL_COV, cR = cov?.right ?? FULL_COV;
    // One texture clone per FULL surface (floor / back / side) so repeats never clash.
    const floorTex = useSurfaceTexture(textureUrl, rep(W, tileW), rep(D, tileH), gridBearingDeg);
    const backTex = useSurfaceTexture(textureUrl, rep(W, tileW), rep(H, tileH));
    const sideTex = useSurfaceTexture(textureUrl, rep(D, tileW), rep(H, tileH));
    // One clone per AREA panel — its repeat tracks the panel's real-world size so
    // the tile density stays consistent with the full surface.
    const floorPanelTex = useSurfaceTexture(textureUrl, rep((cF.u1 - cF.u0) * W, tileW), rep((cF.v1 - cF.v0) * D, tileH), gridBearingDeg);
    const backPanelTex = useSurfaceTexture(textureUrl, rep((cB.u1 - cB.u0) * W, tileW), rep((cB.v1 - cB.v0) * H, tileH));
    const leftPanelTex = useSurfaceTexture(textureUrl, rep((cL.u1 - cL.u0) * D, tileW), rep((cL.v1 - cL.v0) * H, tileH));
    const rightPanelTex = useSurfaceTexture(textureUrl, rep((cR.u1 - cR.u0) * D, tileW), rep((cR.v1 - cR.v0) * H, tileH));
    // A cloned texture that was never rendered (its surface was neutral) isn't
    // uploaded to the GPU, so it shows white the first frame it becomes material.
    // Force an upload + redraw whenever the material assignment changes.
    const invalidate = useThree((s) => s.invalidate);
    const camera = useThree((s) => s.camera);
    const gl = useThree((s) => s.gl);
    useEffect(() => {
        [floorTex, backTex, sideTex, floorPanelTex, backPanelTex, leftPanelTex, rightPanelTex].forEach((t) => { t.needsUpdate = true; });
        invalidate();
    }, [matFloor, matBack, matLeft, matRight, aF, aB, aL, aR, floorTex, backTex, sideTex, floorPanelTex, backPanelTex, leftPanelTex, rightPanelTex, invalidate]);
    // Hovered surface (viewport highlight + click-to-clad) and the live-dimension
    // label shown while a structure handle is being dragged.
    const [hoverSurf, setHoverSurf] = useState<SurfaceKey | null>(null);
    const [dragInfo, setDragInfo] = useState<null | 'floor' | 'height'>(null);
    // Colour-coded placeholders so the reskin AI can tell surfaces apart: walls
    // stay light GREY (also what the worker's wall-detection keys on), the floor
    // is a distinct warm TAN. This stops the material leaking onto the floor and
    // stops a wall (or the area above a mid-wall backsplash) being read as ceiling.
    const neutralWall = '#D8D8D4';
    const neutralFloor = '#C7BCA6';
    // Distinct keys so switching material ⇄ neutral REMOUNTS the material — adding
    // a `map` to an existing material doesn't recompile its shader (stays white).
    // `hovered` gives a soft neutral brighten (white emissive lift) — an elegant,
    // colourless highlight rather than a toy tint (same material, no remount).
    const matMat = (tex: THREE.Texture, hovered = false) => <meshStandardMaterial key="mat" map={tex} roughness={0.85} metalness={0} emissive="#FFFFFF" emissiveIntensity={hovered ? 0.06 : 0} />;
    const neutralMat = (color: string, hovered = false) => <meshStandardMaterial key="neu" color={color} roughness={1} metalness={0} emissive="#FFFFFF" emissiveIntensity={hovered ? 0.09 : 0} />;
    // Viewport surface interaction: hover highlights, a clean CLICK (no drag —
    // R3F's e.delta is the px distance between down and up) toggles the material.
    const surfEvents = (s: SurfaceKey) => (!showHandles || !onToggleSurface) ? {} : {
        onPointerOver: (e: any) => { setHoverSurf(s); document.body.style.cursor = 'pointer'; },
        onPointerOut: () => { setHoverSurf((h) => (h === s ? null : h)); document.body.style.cursor = ''; },
        onClick: (e: any) => { if (e.delta <= 5) { e.stopPropagation(); onToggleSurface(s); } },
    };
    const w = walls || { back: true, left: true, right: false };
    const anyWall = w.back || w.left || w.right;
    const clampFloor = (v: number) => Math.min(24, Math.max(2, v));
    const clampWall = (v: number) => Math.min(6, Math.max(1.5, v));
    const emit = onResize || (() => { });
    const active = onDragActive || (() => { });

    // ── Area-panel geometry. Each surface maps a normalised (u,v) rect to a
    // world-space sub-plane sitting just in front of the surface, plus the plane
    // + corner→world + world→(u,v) maps its drag handles need.
    const EPS = 0.008;
    const planes: Record<SurfaceKey, THREE.Plane> = {
        floor: new THREE.Plane(new THREE.Vector3(0, 1, 0), 0),
        back: new THREE.Plane(new THREE.Vector3(0, 0, 1), D / 2),
        left: new THREE.Plane(new THREE.Vector3(1, 0, 0), W / 2),
        right: new THREE.Plane(new THREE.Vector3(1, 0, 0), -W / 2),
    };
    const cornerWorld = (s: SurfaceKey, u: number, v: number): [number, number, number] => {
        if (s === 'floor') return [-W / 2 + u * W, 0.04, -D / 2 + v * D];
        if (s === 'back') return [-W / 2 + u * W, v * H, -D / 2 + 0.02];
        if (s === 'left') return [-W / 2 + 0.02, v * H, -D / 2 + u * D];
        return [W / 2 - 0.02, v * H, -D / 2 + u * D];
    };
    const worldToUV = (s: SurfaceKey, p: THREE.Vector3): { u: number; v: number } => {
        if (s === 'floor') return { u: (p.x + W / 2) / W, v: (p.z + D / 2) / D };
        if (s === 'back') return { u: (p.x + W / 2) / W, v: p.y / H };
        return { u: (p.z + D / 2) / D, v: p.y / H }; // left / right
    };
    const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
    const dragCorner = (s: SurfaceKey, corner: 'a' | 'b' | 'c' | 'd', p: THREE.Vector3, c: Coverage) => {
        const { u, v } = worldToUV(s, p);
        const nu = clamp01(u), nv = clamp01(v);
        let { u0, v0, u1, v1 } = c;
        if (corner === 'a') { u0 = Math.min(nu, u1 - MIN_COV); v0 = Math.min(nv, v1 - MIN_COV); }
        else if (corner === 'b') { u1 = Math.max(nu, u0 + MIN_COV); v0 = Math.min(nv, v1 - MIN_COV); }
        else if (corner === 'c') { u1 = Math.max(nu, u0 + MIN_COV); v1 = Math.max(nv, v0 + MIN_COV); }
        else { u0 = Math.min(nu, u1 - MIN_COV); v1 = Math.max(nv, v0 + MIN_COV); }
        onCov?.(s, { u0: clamp01(u0), v0: clamp01(v0), u1: clamp01(u1), v1: clamp01(v1) });
    };
    // Drag the panel BODY to move the whole area (size unchanged), clamped so the
    // rect stays inside its surface.
    const startPanelMove = (e: any, s: SurfaceKey, c: Coverage) => {
        if (!showHandles) return;
        e.stopPropagation();
        active(true);
        const rect = gl.domElement.getBoundingClientRect();
        const plane = planes[s];
        const raycaster = new THREE.Raycaster();
        const ndc = new THREE.Vector2();
        const hit = new THREE.Vector3();
        const wSpan = c.u1 - c.u0, hSpan = c.v1 - c.v0;
        const toUV = (cx: number, cy: number) => {
            ndc.x = ((cx - rect.left) / rect.width) * 2 - 1;
            ndc.y = -((cy - rect.top) / rect.height) * 2 + 1;
            raycaster.setFromCamera(ndc, camera);
            return raycaster.ray.intersectPlane(plane, hit) ? worldToUV(s, hit) : null;
        };
        const start = toUV(e.clientX, e.clientY);
        if (!start) { active(false); return; }
        const offU = start.u - (c.u0 + c.u1) / 2;
        const offV = start.v - (c.v0 + c.v1) / 2;
        const move = (ev: PointerEvent) => {
            const p = toUV(ev.clientX, ev.clientY);
            if (!p) return;
            const cu = Math.min(1 - wSpan / 2, Math.max(wSpan / 2, p.u - offU));
            const cv = Math.min(1 - hSpan / 2, Math.max(hSpan / 2, p.v - offV));
            onCov?.(s, { u0: cu - wSpan / 2, v0: cv - hSpan / 2, u1: cu + wSpan / 2, v1: cv + hSpan / 2 });
        };
        const up = () => { active(false); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    };
    const panelEvents = (s: SurfaceKey, c: Coverage) => (!showHandles ? {} : {
        onPointerDown: (e: any) => startPanelMove(e, s, c),
        // Panel is a "move" target — swallow the hover so the base surface
        // underneath doesn't light up as a click-to-clad target.
        onPointerOver: (e: any) => { e.stopPropagation(); document.body.style.cursor = 'move'; },
        onPointerOut: () => { document.body.style.cursor = ''; },
    });
    // Emerald outline so the area reads as a selected, editable region.
    const panelOutline = (s: SurfaceKey, c: Coverage) => {
        if (!showHandles) return null;
        const pts: Array<[number, number, number]> = [
            cornerWorld(s, c.u0, c.v0), cornerWorld(s, c.u1, c.v0),
            cornerWorld(s, c.u1, c.v1), cornerWorld(s, c.u0, c.v1),
            cornerWorld(s, c.u0, c.v0),
        ];
        return <Line userData={{ studioHelper: true }} points={pts} color="#111827" lineWidth={1.5} transparent opacity={0.85} depthTest={false} />;
    };
    // A textured panel mesh for surface `s` covering rect `c`.
    const panelMesh = (s: SurfaceKey, c: Coverage, tex: THREE.Texture) => {
        const uc = (c.u0 + c.u1) / 2, vc = (c.v0 + c.v1) / 2;
        if (s === 'floor') {
            return (
                <mesh key={`fp`} rotation={[-Math.PI / 2, 0, 0]} position={[-W / 2 + uc * W, EPS, -D / 2 + vc * D]} receiveShadow {...panelEvents(s, c)}>
                    <planeGeometry args={[(c.u1 - c.u0) * W, (c.v1 - c.v0) * D]} />
                    {matMat(tex)}
                </mesh>
            );
        }
        if (s === 'back') {
            return (
                <mesh key={`bp`} position={[-W / 2 + uc * W, vc * H, -D / 2 + EPS]} receiveShadow {...panelEvents(s, c)}>
                    <planeGeometry args={[(c.u1 - c.u0) * W, (c.v1 - c.v0) * H]} />
                    {matMat(tex)}
                </mesh>
            );
        }
        const rotY = s === 'left' ? Math.PI / 2 : -Math.PI / 2;
        const px = s === 'left' ? -W / 2 + EPS : W / 2 - EPS;
        return (
            <mesh key={`${s}p`} rotation={[0, rotY, 0]} position={[px, vc * H, -D / 2 + uc * D]} receiveShadow {...panelEvents(s, c)}>
                <planeGeometry args={[(c.u1 - c.u0) * D, (c.v1 - c.v0) * H]} />
                {matMat(tex)}
            </mesh>
        );
    };
    // Corner handles for an area surface (a=bl, b=br, c=tr, d=tl in u,v space).
    const panelHandles = (s: SurfaceKey, c: Coverage) => {
        if (!showHandles) return null;
        const corners: Array<{ k: 'a' | 'b' | 'c' | 'd'; u: number; v: number }> = [
            { k: 'a', u: c.u0, v: c.v0 }, { k: 'b', u: c.u1, v: c.v0 },
            { k: 'c', u: c.u1, v: c.v1 }, { k: 'd', u: c.u0, v: c.v1 },
        ];
        return corners.map((cn) => (
            <PanelHandle key={`${s}-${cn.k}`} position={cornerWorld(s, cn.u, cn.v)} plane={planes[s]} onDragActive={active} onDrag={(p) => dragCorner(s, cn.k, p, c)} />
        ));
    };
    // A clad surface shows its FULL material only when NOT in area mode; in area
    // mode the base stays neutral and the panel carries the material.
    const floorFull = matFloor && !aF;
    const backFull = matBack && !aB;
    const leftFull = matLeft && !aL;
    const rightFull = matRight && !aR;
    // Structure drags also feed the live-dimension label.
    const structActive = (kind: 'floor' | 'height') => (a: boolean) => { active(a); setDragInfo(a ? kind : null); };
    return (
        <group>
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow {...surfEvents('floor')}>
                <planeGeometry key={`f-${W}-${D}`} args={[W, D]} />
                {floorFull ? matMat(floorTex, hoverSurf === 'floor') : neutralMat(neutralFloor, hoverSurf === 'floor')}
            </mesh>
            {matFloor && aF && panelMesh('floor', cF, floorPanelTex)}
            {matFloor && aF && panelOutline('floor', cF)}
            {matFloor && aF && panelHandles('floor', cF)}
            {w.back && (
                <mesh position={[0, H / 2, -D / 2]} receiveShadow {...surfEvents('back')}>
                    <planeGeometry key={`b-${W}-${H}`} args={[W, H]} />
                    {backFull ? matMat(backTex, hoverSurf === 'back') : neutralMat(neutralWall, hoverSurf === 'back')}
                </mesh>
            )}
            {w.back && matBack && aB && panelMesh('back', cB, backPanelTex)}
            {w.back && matBack && aB && panelOutline('back', cB)}
            {w.back && matBack && aB && panelHandles('back', cB)}
            {w.left && (
                <mesh rotation={[0, Math.PI / 2, 0]} position={[-W / 2, H / 2, 0]} receiveShadow {...surfEvents('left')}>
                    <planeGeometry key={`l-${D}-${H}`} args={[D, H]} />
                    {leftFull ? matMat(sideTex, hoverSurf === 'left') : neutralMat(neutralWall, hoverSurf === 'left')}
                </mesh>
            )}
            {w.left && matLeft && aL && panelMesh('left', cL, leftPanelTex)}
            {w.left && matLeft && aL && panelOutline('left', cL)}
            {w.left && matLeft && aL && panelHandles('left', cL)}
            {w.right && (
                <mesh rotation={[0, -Math.PI / 2, 0]} position={[W / 2, H / 2, 0]} receiveShadow {...surfEvents('right')}>
                    <planeGeometry key={`r-${D}-${H}`} args={[D, H]} />
                    {rightFull ? matMat(sideTex, hoverSurf === 'right') : neutralMat(neutralWall, hoverSurf === 'right')}
                </mesh>
            )}
            {w.right && matRight && aR && panelMesh('right', cR, rightPanelTex)}
            {w.right && matRight && aR && panelOutline('right', cR)}
            {w.right && matRight && aR && panelHandles('right', cR)}
            {showHandles && (
                <>
                    {/* Floor edge arrows (drag out to expand). Symmetric about centre. */}
                    <AxisArrow position={[W / 2, 0.06, 0]} axis="x" onDragActive={structActive('floor')} onDrag={(x) => emit(clampFloor(2 * Math.abs(x)), D, H)} />
                    <AxisArrow position={[-W / 2, 0.06, 0]} axis="x" onDragActive={structActive('floor')} onDrag={(x) => emit(clampFloor(2 * Math.abs(x)), D, H)} />
                    <AxisArrow position={[0, 0.06, D / 2]} axis="z" onDragActive={structActive('floor')} onDrag={(z) => emit(W, clampFloor(2 * Math.abs(z)), H)} />
                    <AxisArrow position={[0, 0.06, -D / 2]} axis="z" onDragActive={structActive('floor')} onDrag={(z) => emit(W, clampFloor(2 * Math.abs(z)), H)} />
                    {/* Wall height arrow — top back-left corner. */}
                    {anyWall && (
                        <AxisArrow position={[-W / 2, H, -D / 2]} axis="y" onDragActive={structActive('height')} onDrag={(y) => emit(W, D, clampWall(y))} />
                    )}
                    {/* Live dimensions while dragging. */}
                    {dragInfo && (
                        <Html position={dragInfo === 'height' ? [-W / 2, H + 0.4, -D / 2] : [0, 0.35, 0]} center style={{ pointerEvents: 'none' }}>
                            {/* In-scene overlay — fixed dark chip over the 3D render,
                                not themed app chrome. */}
                            <div style={{ fontSize: 11, fontWeight: 500, color: '#FFFFFF', backgroundColor: 'rgba(17,24,39,0.85)', padding: '3px 10px', whiteSpace: 'nowrap' }}>
                                {dragInfo === 'height' ? `${H.toFixed(2)} m` : `${W.toFixed(1)} × ${D.toFixed(1)} m`}
                            </div>
                        </Html>
                    )}
                </>
            )}
        </group>
    );
};

/** Single-piece mode: `count` slabs, each loading materialPaths[i] (cycling /
 *  duplicating). Click one to select it (parent attaches the gizmo). */
const SinglePieceScene: React.FC<{ groundColor?: string; textures: string[]; count: number; layout?: PoseLayout; tileW: number; tileH: number; thickness: number; onSelect: (o: THREE.Object3D | null) => void; capturing?: boolean; savedTransforms?: PieceTransform[]; registerPiece: (i: number, o: THREE.Object3D | null) => void }> = ({ groundColor, textures, count, layout = 'flat', tileW, tileH, thickness, onSelect, capturing, savedTransforms, registerPiece }) => {
    return (
        <group>
            {/* Neutral ground for posing — hidden during capture so the piece(s)
                are a clean transparent cutout the worker composites on. */}
            {!capturing && (
                <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow onClick={(e: any) => { e.stopPropagation(); onSelect(null); }}>
                    <planeGeometry args={[14, 14]} />
                    <meshStandardMaterial color={groundColor || '#DCDCD8'} roughness={1} metalness={0} />
                </mesh>
            )}
            {Array.from({ length: count }).map((_, i) => {
                const at = placePiece(layout, i, count, tileW, tileH, thickness);
                return (
                    <Slab key={i} index={i} url={textures[i % textures.length]} width={tileW} height={tileH} thickness={thickness} initPos={at.pos} initRot={at.rot} saved={savedTransforms?.[i]} register={registerPiece} onSelect={onSelect} />
                );
            })}
            {/* A held piece touches nothing, so a shadow under it is a shadow
                cast by nothing — the giveaway that a product was pasted in. */}
            {!capturing && !isAirborne(layout) && <ContactShadows position={[0, 0.001, 0]} opacity={0.3} scale={10} blur={2.4} far={4} resolution={1024} color="#1A1A1A" />}
        </group>
    );
};

const Slab: React.FC<{ index: number; url: string; width: number; height: number; thickness: number; initPos: [number, number, number]; initRot?: [number, number, number]; saved?: PieceTransform; register: (i: number, o: THREE.Object3D | null) => void; onSelect: (o: THREE.Object3D | null) => void }> = ({ index, url, width, height, thickness, initPos, initRot, saved, register, onSelect }) => {
    const tex = useSurfaceTexture(url, 1, 1);
    const ref = useRef<THREE.Group>(null);
    useEffect(() => {
        const g = ref.current;
        if (g) {
            // Restore a saved gizmo transform if we have one, else lay out at the
            // initial grid position.
            if (saved) {
                g.position.set(saved.pos[0], saved.pos[1], saved.pos[2]);
                g.rotation.set(saved.rot[0], saved.rot[1], saved.rot[2]);
                g.scale.set(saved.scale[0], saved.scale[1], saved.scale[2]);
            } else {
                g.position.set(initPos[0], initPos[1], initPos[2]);
                if (initRot) g.rotation.set(initRot[0], initRot[1], initRot[2]);
            }
            register(index, g);
        }
        return () => register(index, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <group ref={ref}>
            <mesh castShadow receiveShadow onClick={(e: any) => { e.stopPropagation(); if (ref.current) onSelect(ref.current); }}>
                <boxGeometry args={[width, thickness, height]} />
                <meshStandardMaterial attach="material-0" color="#CFCFCB" roughness={0.9} />
                <meshStandardMaterial attach="material-1" color="#CFCFCB" roughness={0.9} />
                <meshStandardMaterial attach="material-2" map={tex} roughness={0.8} metalness={0} />
                <meshStandardMaterial attach="material-3" color="#CFCFCB" roughness={0.9} />
                <meshStandardMaterial attach="material-4" color="#CFCFCB" roughness={0.9} />
                <meshStandardMaterial attach="material-5" color="#CFCFCB" roughness={0.9} />
            </mesh>
        </group>
    );
};
